import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Home, LocateFixed, LogOut, Map, Maximize2, MessageSquare, Minimize2, Network, Newspaper, Pencil, Plus, Settings, Trash2, Users, Wrench, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import LeafletPinPicker from '../../components/LeafletPinPicker';
import SearchableComboBox from '../../components/SearchableComboBox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Circle, MapContainer, Marker, Popup, Polyline, useMap, useMapEvent } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LeafletTileLayer from '../../components/LeafletTileLayer';
import ktcLogo from '../../assets/ktc.png';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import InfraServiceHealthPanel from '../../components/InfraServiceHealthPanel';
import MapStatusPill from '../../components/MapStatusPill';
import AdminGlobalMenuBar from '../../components/AdminGlobalMenuBar';
import LeafletFlowDirection from '../../components/LeafletFlowDirection';
import { getBandwidthStyle, resolveLinkLoad } from '../../components/leafletBandwidthStyle';
import './AdminConnectivityMap.css';
import { getInfraNodePreviewClassName } from '../../components/leafletNetworkIcons';
import { createOLTMarker, createODPMarker, drawConnection } from '../../components/leafletFuturisticNetwork';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import {
  buildCapacityInsights,
  buildCapacityRecommendations,
  buildNasCapacityPredictions,
  buildOdpCapacityPredictions,
  getRecommendationTypeBadgeClass,
  getRecommendationTypeLabel,
  getRiskBadgeClass,
  getUrgencyBadgeClass,
} from '../../utils/capacityPrediction';
import useMinimumLoadingVisibility from '../../hooks/useMinimumLoadingVisibility';
import { normalizeFtthTopologyPayload, normalizeVoucherNasPayload } from './utils/coreEndpointContract';

const API = `${resolveBackendBaseUrl()}/api`;

const MAP_DEFAULT_ZOOM = 15;
const DEFAULT_MAP_CENTER = { latitude: -3.4324, longitude: 119.3430 };
const MATAKALI_FORM_CENTER = { latitude: -3.3869, longitude: 119.3098 };
const FETCH_RETRY_DELAYS_MS = [2000, 5000, 10000];
const DEFAULT_FTTH_HEADER = {
  title: 'Rancangan FTTH',
  subtitle: 'Workspace planning FTTH: desain relasi OLT-ODP-rumah dengan dominasi map untuk perencanaan lapangan.',
};

const DEFAULT_OLT_FORM = { name: '', code: '', router_id: '', kriteria: 'live', location: '', latitude: '', longitude: '', x: '120', y: '140' };
const DEFAULT_ODP_FORM = { name: '', code: '', router_id: '', kriteria: 'live', olt_id: '', address: '', latitude: '', longitude: '', x: '450', y: '180', suggested_capacity: '8' };
const FTTH_CATEGORY_OPTIONS = [
  { value: 'live', label: 'Live (Terpasang)' },
  { value: 'plan', label: 'Plan (Perencanaan)' },
];
const DEFAULT_ODP_CAPACITY = 8;
const ODP_CAPACITY_OPTIONS = [8, 16, 24];
const AI_PLANNING_MAX_CANDIDATES = 3;
const EXPLORER_CUSTOMER_PAGE_SIZE = 20;

const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeInfraKriteria = (value) => (String(value || 'live').trim().toLowerCase() === 'plan' ? 'plan' : 'live');
const getInfraCategoryLabel = (value) => (normalizeInfraKriteria(value) === 'plan' ? 'Plan' : 'Live');
const getInfraCategoryTheme = (value) => {
  const category = normalizeInfraKriteria(value);
  if (category === 'plan') {
    return {
      category,
      color: '#f59e0b',
      border: 'rgba(245, 158, 11, 0.45)',
      bg: 'rgba(245, 158, 11, 0.12)',
      text: '#fcd34d',
    };
  }

  return {
    category,
    color: '#3b82f6',
    border: 'rgba(59, 130, 246, 0.45)',
    bg: 'rgba(59, 130, 246, 0.12)',
    text: '#93c5fd',
  };
};

const getStatusColor = (status) => {
  if (status === 'online') return '#3b82f6';
  if (status === 'offline') return '#ef4444';
  if (status === 'not_mapped') return '#f59e0b';
  return '#9ca3af';
};

const createWifiIcon = (status, ai = { anomaly: false, predictive: false }) => {
  const stateClass = status === 'online' ? 'online' : status === 'offline' ? 'offline' : 'unknown';
  const anomalyClass = ai?.anomaly ? ' wifi-anomaly node-alert' : '';
  const predictiveBadge = ai?.predictive ? '<span class="wifi-predict-badge">AI</span>' : '';

  return L.divIcon({
    className: 'wifi-marker-wrapper',
    html: `<div class=\"wifi-marker wifi-${stateClass}${anomalyClass}\">${predictiveBadge}<span class=\"wifi-pulse\"></span><svg viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M12 20h.01\"></path><path d=\"M2 8.82a15 15 0 0 1 20 0\"></path><path d=\"M5 12.86a10 10 0 0 1 14 0\"></path><path d=\"M8.5 16.43a5 5 0 0 1 7 0\"></path></svg></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });
};

const createPlanningCandidateIcon = (candidate, selected = false) => {
  const capacity = Number(candidate?.suggestedCapacity || DEFAULT_ODP_CAPACITY);
  const rank = String(candidate?.rank || '?');
  const score = Number(candidate?.totalScore || 0);
  const borderClass = selected ? 'ring-2 ring-cyan-300' : '';
  return L.divIcon({
    className: 'planning-candidate-marker-wrapper',
    html: `
      <div class="planning-candidate-marker ${selected ? 'is-selected' : ''} ${borderClass}">
        <span class="planning-candidate-rank">${rank}</span>
        <span class="planning-candidate-cap">${capacity}</span>
        <span class="planning-candidate-score">${score}</span>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -16],
  });
};

const parseLastInformLatencySeconds = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (!Number.isFinite(diffSeconds) || diffSeconds < 0) return null;
  return diffSeconds;
};

const parseNodeRxPower = (node) => {
  const candidates = [node?.rx_power_dbm, node?.rx_power, node?.status_detail?.rx_power_dbm, node?.status_detail?.rx_power];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const detectAnomaly = (node) => {
  const issues = [];
  const status = String(node?.status || node?.connection_status || '').toLowerCase();
  const latency = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rx = parseNodeRxPower(node);
  if (status === 'offline' || status === 'unavailable') issues.push({ code: 'offline', label: 'Node offline' });
  if (Number.isFinite(latency) && latency > 80) issues.push({ code: 'latency_high', label: `Latency tinggi (${latency}s)` });
  if (Number.isFinite(rx) && rx < -25) issues.push({ code: 'signal_weak', label: `RX power lemah (${rx.toFixed(2)} dBm)` });
  return { hasAnomaly: issues.length > 0, issues };
};

const predictIssue = (node) => {
  const predictions = [];
  const latency = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rx = parseNodeRxPower(node);
  if (Number.isFinite(rx) && rx < -27) predictions.push('Signal berpotensi drop');
  if (Number.isFinite(latency) && latency > 120) predictions.push('Potensi congestion');
  return predictions;
};

const estimateCustomerLoad = (customer = {}) => {
  const direct = resolveLinkLoad(customer);
  if (direct > 0) return direct;
  const stations = Number(customer?.status_detail?.total_stations);
  const isOnline = String(customer?.status || customer?.connection_status || '').toLowerCase() === 'online';
  if (Number.isFinite(stations) && stations > 0) {
    return Math.min(100, stations * 16 + (isOnline ? 18 : 0));
  }
  if (isOnline) return 42;
  if (String(customer?.status || customer?.connection_status || '').toLowerCase() === 'offline') return 0;
  return 14;
};

const FitBoundsController = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(points) || points.length === 0) return;

    if (points.length === 1) {
      map.setView(points[0], 16, { animate: true });
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);

  return null;
};

const MapClickPicker = ({ enabled, onPick }) => {
  useMapEvent('click', (event) => {
    if (!enabled || typeof onPick !== 'function') return;
    const latitude = Number(event?.latlng?.lat);
    const longitude = Number(event?.latlng?.lng);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    onPick(latitude, longitude);
  });
  return null;
};

const normalizeTopologyCustomer = (raw) => {
  const customer = raw || {};
  const normalizedEstimated = Number(customer.estimated_attenuation_db);
  const normalizedCount = Number(customer.odp_customer_count);
  const normalizedCapacity = Number(customer.odp_capacity);
  return {
    ...customer,
    linked_odp_id: customer.linked_odp_id || customer.odp_id || null,
    linked_olt_id: customer.linked_olt_id || customer.olt_id || null,
    status: customer.status || customer.connection_status || 'unknown',
    estimated_attenuation_db: Number.isFinite(normalizedEstimated) ? normalizedEstimated : null,
    odp_customer_count: Number.isFinite(normalizedCount) ? normalizedCount : null,
    odp_capacity: Number.isFinite(normalizedCapacity) ? normalizedCapacity : DEFAULT_ODP_CAPACITY,
    odp_over_capacity: Boolean(customer.odp_over_capacity),
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toRadians = (value) => (Number(value) * Math.PI) / 180;

const distanceMeters = (pointA, pointB) => {
  if (!Array.isArray(pointA) || !Array.isArray(pointB) || pointA.length < 2 || pointB.length < 2) return Number.POSITIVE_INFINITY;
  const [lat1, lon1] = pointA.map(Number);
  const [lat2, lon2] = pointB.map(Number);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const hasValidCoordinates = (lat, lon) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const buildDistributionPath = (fromPoint, toPoint, seed = '') => {
  if (!Array.isArray(fromPoint) || !Array.isArray(toPoint)) return [fromPoint, toPoint];
  const fromLat = Number(fromPoint[0]);
  const fromLon = Number(fromPoint[1]);
  const toLat = Number(toPoint[0]);
  const toLon = Number(toPoint[1]);
  if (!Number.isFinite(fromLat) || !Number.isFinite(fromLon) || !Number.isFinite(toLat) || !Number.isFinite(toLon)) {
    return [fromPoint, toPoint];
  }
  const hash = String(seed || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const direction = hash % 2 === 0 ? 1 : -1;
  const curveStrength = 0.00012 + (hash % 7) * 0.00001;
  const midLat = (fromLat + toLat) / 2;
  const midLon = (fromLon + toLon) / 2;
  const ctrlLat = midLat + (toLon - fromLon) * curveStrength * direction;
  const ctrlLon = midLon - (toLat - fromLat) * curveStrength * direction;
  return [
    [fromLat, fromLon],
    [ctrlLat, ctrlLon],
    [toLat, toLon],
  ];
};

const buildOverlapPositionLookup = (items) => {
  const groups = {};
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = String(item?.id || '').trim();
    const lat = Number(item?.latitude);
    const lon = Number(item?.longitude);
    if (!id || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
    const key = `${lat.toFixed(4)}:${lon.toFixed(4)}`;
    if (!Array.isArray(groups[key])) groups[key] = [];
    groups[key].push({ id, lat, lon });
  });

  const lookup = {};
  Object.values(groups).forEach((group) => {
    const sorted = group.slice().sort((a, b) => a.id.localeCompare(b.id));
    if (sorted.length === 1) {
      lookup[sorted[0].id] = [sorted[0].lat, sorted[0].lon];
      return;
    }
    sorted.forEach((node, idx) => {
      const angle = (Math.PI * 2 * idx) / sorted.length;
      const radiusMeters = 12 + Math.floor(idx / 8) * 8;
      const latOffset = (radiusMeters * Math.sin(angle)) / 111320;
      const lonOffset = (radiusMeters * Math.cos(angle)) / (111320 * Math.max(Math.cos((node.lat * Math.PI) / 180), 0.2));
      lookup[node.id] = [node.lat + latOffset, node.lon + lonOffset];
    });
  });
  return lookup;
};

const formatCoordinate = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(6) : '-');

const formatSignal = (customer) => {
  if (Number.isFinite(Number(customer?.attenuation_db))) return `${Number(customer.attenuation_db).toFixed(2)} dB`;
  if (Number.isFinite(Number(customer?.rx_power_dbm))) return `${Number(customer.rx_power_dbm).toFixed(2)} dBm`;
  return 'Tidak tersedia';
};

const latLonToPixel = (lat, lon, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const pixelToLatLon = (x, y, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return {
    latitude: clamp(lat, -85.05112878, 85.05112878),
    longitude: clamp(lon, -180, 180),
  };
};

const getOsmEmbedUrl = (centerLat, centerLon, zoom, width, height) => {
  const safeWidth = clamp(Math.round(width || 640), 320, 1200);
  const safeHeight = clamp(Math.round(height || 280), 200, 800);

  const centerPixel = latLonToPixel(centerLat, centerLon, zoom);
  const topLeft = pixelToLatLon(centerPixel.x - safeWidth / 2, centerPixel.y - safeHeight / 2, zoom);
  const bottomRight = pixelToLatLon(centerPixel.x + safeWidth / 2, centerPixel.y + safeHeight / 2, zoom);

  const minLon = topLeft.longitude;
  const minLat = bottomRight.latitude;
  const maxLon = bottomRight.longitude;
  const maxLat = topLeft.latitude;

  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon}%2C${minLat}%2C${maxLon}%2C${maxLat}&layer=mapnik`;
};

const StaticMapPinPicker = ({ title, latitude, longitude, onChange }) => {
  const mapRef = useRef(null);
  const skipAutoCenterRef = useRef(false);

  const [zoom, setZoom] = useState(MAP_DEFAULT_ZOOM);
  const [isDraggingPin, setIsDraggingPin] = useState(false);
  const [mapSize, setMapSize] = useState({ width: 640, height: 280 });
  const [center, setCenter] = useState(() => {
    if (hasValidCoordinates(latitude, longitude)) {
      return { latitude: Number(latitude), longitude: Number(longitude) };
    }
    return DEFAULT_MAP_CENTER;
  });

  const marker = useMemo(() => {
    if (hasValidCoordinates(latitude, longitude)) {
      return { latitude: Number(latitude), longitude: Number(longitude) };
    }
    return center;
  }, [latitude, longitude, center]);

  useEffect(() => {
    if (!mapRef.current || typeof ResizeObserver === 'undefined') return undefined;

    const updateSize = () => {
      if (!mapRef.current) return;
      const rect = mapRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setMapSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(mapRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasValidCoordinates(latitude, longitude)) return;
    if (skipAutoCenterRef.current) {
      skipAutoCenterRef.current = false;
      return;
    }

    setCenter({ latitude: Number(latitude), longitude: Number(longitude) });
  }, [latitude, longitude]);

  const updateLocationFromPointer = useCallback((clientX, clientY) => {
    if (!mapRef.current) return;

    const rect = mapRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const localX = clamp(clientX - rect.left, 0, rect.width);
    const localY = clamp(clientY - rect.top, 0, rect.height);

    const centerPixel = latLonToPixel(center.latitude, center.longitude, zoom);
    const nextPixelX = centerPixel.x + (localX - rect.width / 2);
    const nextPixelY = centerPixel.y + (localY - rect.height / 2);

    const nextLocation = pixelToLatLon(nextPixelX, nextPixelY, zoom);
    skipAutoCenterRef.current = true;
    onChange(nextLocation.latitude, nextLocation.longitude);
  }, [center.latitude, center.longitude, onChange, zoom]);

  useEffect(() => {
    if (!isDraggingPin) return undefined;

    const onMove = (event) => updateLocationFromPointer(event.clientX, event.clientY);
    const onUp = () => setIsDraggingPin(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingPin, updateLocationFromPointer]);

  const markerPosition = useMemo(() => {
    const centerPixel = latLonToPixel(center.latitude, center.longitude, zoom);
    const markerPixel = latLonToPixel(marker.latitude, marker.longitude, zoom);

    return {
      x: mapSize.width / 2 + (markerPixel.x - centerPixel.x),
      y: mapSize.height / 2 + (markerPixel.y - centerPixel.y),
    };
  }, [center.latitude, center.longitude, mapSize.height, mapSize.width, marker.latitude, marker.longitude, zoom]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-400">
        <p>{title}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setZoom((prev) => clamp(prev - 1, 3, 19))}
            className="h-7 w-7 rounded border border-white/20 text-white hover:bg-white/10"
          >
            -
          </button>
          <span className="text-xs">Zoom {zoom}</span>
          <button
            type="button"
            onClick={() => setZoom((prev) => clamp(prev + 1, 3, 19))}
            className="h-7 w-7 rounded border border-white/20 text-white hover:bg-white/10"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => setCenter(marker)}
            className="h-7 px-2 rounded border border-white/20 text-white hover:bg-white/10"
          >
            Pusatkan Pin
          </button>
        </div>
      </div>

      <div
        ref={mapRef}
        onClick={(event) => {
          if (isDraggingPin) return;
          updateLocationFromPointer(event.clientX, event.clientY);
        }}
        className="relative h-56 w-full overflow-hidden rounded-lg border border-white/10 cursor-crosshair select-none bg-[#0A0A0A]"
      >
        <iframe
          title={`${title}-map`}
          src={getOsmEmbedUrl(center.latitude, center.longitude, zoom, mapSize.width, mapSize.height)}
          className="h-full w-full border-0 pointer-events-none"
          loading="lazy"
        />

        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDraggingPin(true);
          }}
          className="absolute z-10 cursor-grab active:cursor-grabbing"
          style={{
            left: `${markerPosition.x}px`,
            top: `${markerPosition.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
          aria-label="Pin lokasi"
        >
          <span className="block h-4 w-4 rounded-full bg-red-500 border-2 border-white shadow" />
          <span className="mx-auto block h-2 w-1 rounded-b bg-red-500" />
        </button>
      </div>

      <p className="text-xs text-gray-500">Klik peta atau geser pin untuk memindahkan lokasi.</p>
    </div>
  );
};

const AdminFtthDesign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const showLoadingOverlay = useMinimumLoadingVisibility(loading, 1200);
  const [nasRouters, setNasRouters] = useState([]);
  const [olts, setOlts] = useState([]);
  const [odps, setOdps] = useState([]);
  const [nasLookupItems, setNasLookupItems] = useState([]);
  const [oltLookupItems, setOltLookupItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [serverAvailable, setServerAvailable] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [headerSettings, setHeaderSettings] = useState({ ...DEFAULT_FTTH_HEADER });
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ ...DEFAULT_FTTH_HEADER });
  const [savingHeader, setSavingHeader] = useState(false);

  const [oltForm, setOltForm] = useState({ ...DEFAULT_OLT_FORM });
  const [odpForm, setOdpForm] = useState({ ...DEFAULT_ODP_FORM });
  const [editingOltId, setEditingOltId] = useState(null);
  const [editingOdpId, setEditingOdpId] = useState(null);
  const [oltStep, setOltStep] = useState(1);
  const [odpStep, setOdpStep] = useState(1);
  const [infraFilter, setInfraFilter] = useState('all');
  const [selectedNasId, setSelectedNasId] = useState('all');
  const [selectedOltId, setSelectedOltId] = useState('all');
  const [systemMode, setSystemMode] = useState('design');
  const [infraFlowTab, setInfraFlowTab] = useState('olt');
  const [viewMode, setViewMode] = useState('explorer');
  const [ftthContext, setFtthContext] = useState('live'); // live | planning
  const [planningAction, setPlanningAction] = useState('none'); // none | olt | odp | ai-odp
  const [showCompareLayer, setShowCompareLayer] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState('legend'); // insight | capacity | asset | legend | validation | draft
  const [showExplorerWorkspace, setShowExplorerWorkspace] = useState(false);
  const [aiPlanningUseCase, setAiPlanningUseCase] = useState('next-point'); // next-point | density
  const [aiPlanningStrategy, setAiPlanningStrategy] = useState('balanced'); // cost | balanced | growth
  const [aiAllowedCapacity, setAiAllowedCapacity] = useState({ 8: true, 16: true, 24: true });
  const [aiPlanningRadius, setAiPlanningRadius] = useState(220);
  const [aiPlanningCandidates, setAiPlanningCandidates] = useState([]);
  const [aiPlanningSelectedCandidateId, setAiPlanningSelectedCandidateId] = useState('');
  const [aiPlanningComparedIds, setAiPlanningComparedIds] = useState([]);
  const [aiPlanningLastRunAt, setAiPlanningLastRunAt] = useState('');
  const [aiPlanningWarnings, setAiPlanningWarnings] = useState([]);
  const [aiModeEnabled, setAiModeEnabled] = useState(false);
  const [focusedNode, setFocusedNode] = useState(null);
  const [topologyGraphMeta, setTopologyGraphMeta] = useState(null);
  const [explorerQuery, setExplorerQuery] = useState('');
  const [explorerOltId, setExplorerOltId] = useState('all');
  const [explorerOdpId, setExplorerOdpId] = useState('all');
  const [explorerIssueOnly, setExplorerIssueOnly] = useState(false);
  const [explorerCustomerLimit, setExplorerCustomerLimit] = useState(EXPLORER_CUSTOMER_PAGE_SIZE);
  const [isRetryScheduled, setIsRetryScheduled] = useState(false);
  const authExpiredNotifiedRef = useRef(false);
  const fetchRetryAttemptRef = useRef(0);
  const fetchRetryTimerRef = useRef(null);
  const mapFullscreenRef = useRef(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const deepLinkHandledRef = useRef('');

  const handleAuthExpired = useCallback(() => {
    if (authExpiredNotifiedRef.current) return;
    authExpiredNotifiedRef.current = true;
    clearStaffSession();
    toast.error('Sesi login berakhir. Silakan login ulang.');
    navigate('/dadu/login');
  }, [navigate]);

  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      handleAuthExpired();
      return '';
    }
    return token;
  }, [handleAuthExpired]);

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAuthToken]);

  const syncMapFullscreenState = useCallback(() => {
    const container = mapFullscreenRef.current;
    setIsMapFullscreen(Boolean(container) && document.fullscreenElement === container);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => syncMapFullscreenState();
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [syncMapFullscreenState]);

  const handleToggleMapFullscreen = useCallback(async () => {
    try {
      if (!isMapFullscreen) {
        const container = mapFullscreenRef.current;
        if (container?.requestFullscreen) {
          await container.requestFullscreen();
        }
      } else if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (error) {
      toast.error('Gagal mengubah mode fullscreen peta.');
    }
  }, [isMapFullscreen]);

  const isSuperadmin = useMemo(() => {
    const role = String(user?.role || '').trim().toLowerCase();
    return role === 'superadmin' || role === 'admin' || role === 'administrator';
  }, [user]);

  const handleApiError = useCallback((error, fallbackMessage, options = {}) => {
    const { silent = false } = options;
    if (error?.response?.status === 401) {
      handleAuthExpired();
      return true;
    }
    if (!silent) {
      toast.error(error?.response?.data?.detail || fallbackMessage);
    }
    return false;
  }, [handleAuthExpired]);

  const clearFetchRetry = useCallback(() => {
    fetchRetryAttemptRef.current = 0;
    if (fetchRetryTimerRef.current) {
      window.clearTimeout(fetchRetryTimerRef.current);
      fetchRetryTimerRef.current = null;
    }
    setIsRetryScheduled(false);
  }, []);

  const resetOltForm = () => {
    setEditingOltId(null);
    setOltForm({ ...DEFAULT_OLT_FORM });
    setOltStep(1);
  };

  const resetOdpForm = () => {
    setEditingOdpId(null);
    setOdpForm({ ...DEFAULT_ODP_FORM });
    setOdpStep(1);
  };

  const handleEditOlt = (olt) => {
    setEditingOltId(olt.id);
    setOltStep(1);
    setOltForm({
      name: toInputValue(olt.name),
      code: toInputValue(olt.code),
      router_id: toInputValue(olt.router_id),
      kriteria: normalizeInfraKriteria(olt.kriteria),
      location: toInputValue(olt.location),
      latitude: toInputValue(olt.latitude),
      longitude: toInputValue(olt.longitude),
      x: toInputValue(olt.x ?? DEFAULT_OLT_FORM.x),
      y: toInputValue(olt.y ?? DEFAULT_OLT_FORM.y),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditOdp = (odp) => {
    const linkedOlt = olts.find((item) => String(item.id) === String(odp.olt_id));
    const inferredRouterId = toInputValue(odp.router_id || linkedOlt?.router_id || '');
    setEditingOdpId(odp.id);
    setOdpStep(1);
    setOdpForm({
      name: toInputValue(odp.name),
      code: toInputValue(odp.code),
      router_id: inferredRouterId,
      kriteria: normalizeInfraKriteria(odp.kriteria),
      olt_id: toInputValue(odp.olt_id),
      address: toInputValue(odp.address),
      latitude: toInputValue(odp.latitude),
      longitude: toInputValue(odp.longitude),
      x: toInputValue(odp.x ?? DEFAULT_ODP_FORM.x),
      y: toInputValue(odp.y ?? DEFAULT_ODP_FORM.y),
      suggested_capacity: toInputValue(odp.odp_capacity || DEFAULT_ODP_CAPACITY),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditCustomer = (customer) => {
    if (!customer?.id) return;
    navigate('/dadu/pelanggan', { state: { editCustomerId: customer.id } });
  };

  const handleSystemModeChange = (mode) => {
    const nextMode = String(mode || 'design').toLowerCase();
    setSystemMode(nextMode);
    if (nextMode === 'design') {
      setViewMode('explorer');
      setExplorerIssueOnly(false);
      return;
    }
    if (nextMode === 'monitor') {
      setViewMode('explorer');
      setExplorerIssueOnly(false);
      setAiModeEnabled(false);
      return;
    }
    if (nextMode === 'analyze') {
      setViewMode('validation');
      setExplorerIssueOnly(true);
      return;
    }
    if (nextMode === 'optimize') {
      setViewMode('validation');
      setExplorerIssueOnly(true);
      setAiModeEnabled(true);
      return;
    }
  };

  const handleFtthContextChange = useCallback((nextContext) => {
    const normalized = String(nextContext || 'live').toLowerCase() === 'planning' ? 'planning' : 'live';
    setFtthContext(normalized);
    setShowCompareLayer(false);
    if (normalized === 'planning') {
      setInfraFilter((prev) => (prev === 'live' ? 'plan' : prev));
      setSidePanelTab('draft');
      if (planningAction === 'none') setPlanningAction('odp');
      return;
    }
    setInfraFilter((prev) => (prev === 'plan' ? 'live' : prev));
    setPlanningAction('none');
    setSidePanelTab('insight');
  }, [planningAction]);

  const isPathFocused = (oltId, odpId, customerId) => {
    if (!focusedNode || !focusedNode.id) return true;
    const focusId = String(focusedNode.id);
    if (focusedNode.type === 'olt') return String(oltId || '') === focusId;
    if (focusedNode.type === 'odp') return String(odpId || '') === focusId;
    if (focusedNode.type === 'customer') return String(customerId || '') === focusId;
    return true;
  };

  const fetchTopology = async () => {
    let keepLoader = false;
    setLoading(true);
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setLoading(false);
        return;
      }
      const headers = { headers: headersValue };
      const [topologyRes, statusRes, nasRes, graphRes] = await Promise.allSettled([
        axios.get(`${API}/ftth/topology`, headers),
        axios.get(`${API}/genieacs/customer-status`, headers),
        axios.get(`${API}/voucher/nas`, headers),
        axios.get(`${API}/ftth/topology-graph`, headers),
      ]);

      if (topologyRes.status !== 'fulfilled') {
        throw topologyRes.reason;
      }

      const statusPayload = statusRes.status === 'fulfilled' ? (statusRes.value?.data || {}) : {};
      const statusMap = {};
      toArray(statusPayload.statuses).forEach((item) => {
        if (item?.customer_id) statusMap[item.customer_id] = item;
      });
      setServerAvailable(statusRes.status === 'fulfilled' && statusPayload.server_available !== false);
      setLastUpdated(statusPayload.updated_at || new Date().toISOString());
      const nasPayload = normalizeVoucherNasPayload(nasRes.status === 'fulfilled' ? (nasRes.value?.data || {}) : {});
      setNasRouters(toArray(nasPayload.items));
      if (graphRes.status === 'fulfilled') {
        setTopologyGraphMeta(graphRes.value?.data || null);
      }

      const payload = normalizeFtthTopologyPayload(topologyRes.value?.data || {});

      let nextOlts = toArray(payload.olts);
      let nextOdps = toArray(payload.odps);
      let nextCustomers = toArray(payload.customers).map(normalizeTopologyCustomer);

      if (nextOlts.length === 0 || nextOdps.length === 0) {
        const [oltsRes, odpsRes] = await Promise.allSettled([
          axios.get(`${API}/ftth/olts`, headers),
          axios.get(`${API}/ftth/odps`, headers),
        ]);

        if (oltsRes.status === 'fulfilled') {
          nextOlts = toArray(oltsRes.value?.data);
        }
        if (odpsRes.status === 'fulfilled') {
          nextOdps = toArray(odpsRes.value?.data);
        }
      }

      if (nextCustomers.length === 0) {
        try {
          const customersRes = await axios.get(`${API}/customers`, headers);
          nextCustomers = toArray(customersRes.data).map(normalizeTopologyCustomer);
        } catch (_) {
          nextCustomers = [];
        }
      }

      nextCustomers = nextCustomers.map((customer) => {
        const statusDetail = statusMap[customer.id] || null;
        return {
          ...customer,
          status: statusDetail?.connection_status || customer.status || 'unknown',
          connection_status: statusDetail?.connection_status || customer.connection_status || customer.status || 'unknown',
          attenuation_db: statusDetail?.attenuation_db ?? customer.attenuation_db,
          rx_power_dbm: statusDetail?.rx_power_dbm ?? customer.rx_power_dbm,
          status_detail: statusDetail || customer.status_detail || null,
        };
      });

      setCustomers(nextCustomers);
      setOlts(nextOlts);
      setOdps(nextOdps);
      clearFetchRetry();

      if (nextOlts.length === 0 && nextOdps.length === 0 && nextCustomers.length === 0) {
        toast.warning('Data topologi kosong. Pastikan data OLT/ODP dan pelanggan sudah tersimpan.');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        clearFetchRetry();
        handleAuthExpired();
      } else {
        const attempt = fetchRetryAttemptRef.current;
        const retryDelay = FETCH_RETRY_DELAYS_MS[attempt] ?? null;
        if (retryDelay !== null) {
          fetchRetryAttemptRef.current = attempt + 1;
          keepLoader = true;
          if (!fetchRetryTimerRef.current) {
            setIsRetryScheduled(true);
            fetchRetryTimerRef.current = window.setTimeout(() => {
              fetchRetryTimerRef.current = null;
              setIsRetryScheduled(false);
              fetchTopology();
            }, retryDelay);
            if (attempt === 0) {
              toast.warning('Gagal memuat topologi FTTH, mencoba ulang otomatis...');
            }
          }
        } else {
          setIsRetryScheduled(false);
          setCustomers([]);
          setNasRouters([]);
          setOlts([]);
          setOdps([]);
          setServerAvailable(false);
          setLastUpdated(new Date().toISOString());
          handleApiError(error, 'Gagal memuat topologi FTTH');
        }
      }
    } finally {
      if (!keepLoader) setLoading(false);
    }
  };

  const fetchFtthHeaderSettings = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/settings/ui/ftth-header`, { headers: headersValue });
      const payload = response?.data && typeof response.data === 'object' ? response.data : {};
      const next = {
        title: String(payload.title || DEFAULT_FTTH_HEADER.title).trim() || DEFAULT_FTTH_HEADER.title,
        subtitle: String(payload.subtitle || DEFAULT_FTTH_HEADER.subtitle).trim() || DEFAULT_FTTH_HEADER.subtitle,
      };
      setHeaderSettings(next);
      setHeaderDraft(next);
    } catch (error) {
      handleApiError(error, 'Gagal memuat header FTTH', { silent: true });
      setHeaderSettings({ ...DEFAULT_FTTH_HEADER });
      setHeaderDraft({ ...DEFAULT_FTTH_HEADER });
    }
  }, [getHeaders, handleApiError]);

  const handleSaveFtthHeader = async () => {
    if (!isSuperadmin) {
      toast.error('Hanya superadmin yang dapat mengubah header');
      return;
    }
    const title = String(headerDraft.title || '').trim();
    const subtitle = String(headerDraft.subtitle || '').trim();
    if (!title || !subtitle) {
      toast.error('Judul dan subjudul wajib diisi');
      return;
    }
    setSavingHeader(true);
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      await axios.post(`${API}/settings/ui/ftth-header`, { title, subtitle }, { headers: headersValue });
      const next = { title, subtitle };
      setHeaderSettings(next);
      setHeaderDraft(next);
      setEditingHeader(false);
      toast.success('Header FTTH berhasil diperbarui');
    } catch (error) {
      handleApiError(error, 'Gagal menyimpan header FTTH');
    } finally {
      setSavingHeader(false);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }
    fetchTopology();
    fetchFtthHeaderSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearFetchRetry(), [clearFetchRetry]);

  const searchNasLookup = async (query = '') => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setNasLookupItems([]);
        return;
      }
      const params = { limit: 200 };
      if (String(query || '').trim()) params.q = String(query || '').trim();
      const response = await axios.get(`${API}/voucher/nas`, { headers: headersValue, params });
      const items = toArray(response?.data?.items);
      setNasLookupItems(items);
    } catch (error) {
      handleApiError(error, 'Gagal memuat data NAS', { silent: true });
      setNasLookupItems([]);
    }
  };

  const searchOltLookup = async (query = '', routerId = '') => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setOltLookupItems([]);
        return;
      }
      const params = { limit: 300 };
      if (String(query || '').trim()) params.q = String(query || '').trim();
      if (String(routerId || '').trim()) params.router_id = String(routerId || '').trim();
      const response = await axios.get(`${API}/ftth/olts`, { headers: headersValue, params });
      const items = toArray(response?.data);
      setOltLookupItems(items);
    } catch (error) {
      handleApiError(error, 'Gagal memuat data OLT', { silent: true });
      setOltLookupItems([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ktc_token');
    localStorage.removeItem('ktc_user');
    navigate('/dadu/login');
  };

  const handleGetCurrentLocation = (target) => {
    if (!navigator.geolocation) {
      toast.error('Browser tidak mendukung akses lokasi');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude.toFixed(6);
        const longitude = position.coords.longitude.toFixed(6);

        if (target === 'olt') {
          setOltForm((prev) => ({ ...prev, latitude, longitude }));
        } else {
          setOdpForm((prev) => ({ ...prev, latitude, longitude }));
        }

        toast.success('Lokasi terkini berhasil diambil');
      },
      () => toast.error('Gagal mengambil lokasi terkini'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleMapLocationChange = useCallback((target, latitude, longitude) => {
    const next = { latitude: latitude.toFixed(6), longitude: longitude.toFixed(6) };
    if (target === 'olt') {
      setOltForm((prev) => ({ ...prev, ...next }));
    } else {
      setOdpForm((prev) => ({ ...prev, ...next }));
    }
  }, []);

  const handleMainMapPointPick = useCallback((latitude, longitude) => {
    const target = planningAction === 'olt' ? 'olt' : 'odp';
    handleMapLocationChange(target, latitude, longitude);
  }, [handleMapLocationChange, planningAction]);

  const validateOltStep = (step) => {
    if (step === 1 && !String(oltForm.name || '').trim()) {
      toast.error('Nama OLT wajib diisi.');
      return false;
    }
    if (step === 2 && !hasValidCoordinates(oltForm.latitude, oltForm.longitude)) {
      toast.error('Koordinat OLT belum valid.');
      return false;
    }
    return true;
  };

  const validateOdpStep = (step) => {
    if (step === 1) {
      if (!String(odpForm.name || '').trim()) {
        toast.error('Nama ODP wajib diisi.');
        return false;
      }
      if (!String(odpForm.olt_id || '').trim()) {
        toast.error('Pilih OLT untuk ODP.');
        return false;
      }
    }
    if (step === 2 && !hasValidCoordinates(odpForm.latitude, odpForm.longitude)) {
      toast.error('Koordinat ODP belum valid.');
      return false;
    }
    return true;
  };

  const handleOltNextStep = () => {
    if (!validateOltStep(oltStep)) return;
    setOltStep((prev) => Math.min(3, prev + 1));
  };

  const handleOltPrevStep = () => setOltStep((prev) => Math.max(1, prev - 1));

  const handleOdpNextStep = () => {
    if (!validateOdpStep(odpStep)) return;
    setOdpStep((prev) => Math.min(3, prev + 1));
  };

  const handleOdpPrevStep = () => setOdpStep((prev) => Math.max(1, prev - 1));

  const handleAddOlt = async (e) => {
    e.preventDefault();
    if (oltStep < 3) {
      toast.error('Selesaikan wizard OLT hingga langkah 3 sebelum simpan.');
      return;
    }

    const payload = {
      name: oltForm.name,
      code: oltForm.code || null,
      router_id: oltForm.router_id || null,
      kriteria: normalizeInfraKriteria(oltForm.kriteria),
      location: oltForm.location || null,
      latitude: oltForm.latitude !== '' ? Number(oltForm.latitude) : null,
      longitude: oltForm.longitude !== '' ? Number(oltForm.longitude) : null,
      x: Number(oltForm.x || DEFAULT_OLT_FORM.x),
      y: Number(oltForm.y || DEFAULT_OLT_FORM.y),
    };

    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      if (editingOltId) {
        await axios.put(`${API}/ftth/olts/${editingOltId}`, payload, { headers: headersValue });
        toast.success('OLT berhasil diperbarui');
      } else {
        await axios.post(`${API}/ftth/olts`, payload, { headers: headersValue });
        toast.success('OLT berhasil ditambahkan');
      }

      resetOltForm();
      fetchTopology();
    } catch (error) {
      handleApiError(error, editingOltId ? 'Gagal memperbarui OLT' : 'Gagal menambah OLT');
    }
  };

  const handleAddOdp = async (e) => {
    e.preventDefault();
    if (odpStep < 3) {
      toast.error('Selesaikan wizard ODP hingga langkah 3 sebelum simpan.');
      return;
    }

    const payload = {
      name: odpForm.name,
      code: odpForm.code || null,
      router_id: odpForm.router_id || null,
      kriteria: normalizeInfraKriteria(odpForm.kriteria),
      olt_id: odpForm.olt_id || null,
      address: odpForm.address || null,
      latitude: odpForm.latitude !== '' ? Number(odpForm.latitude) : null,
      longitude: odpForm.longitude !== '' ? Number(odpForm.longitude) : null,
      x: Number(odpForm.x || DEFAULT_ODP_FORM.x),
      y: Number(odpForm.y || DEFAULT_ODP_FORM.y),
    };

    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      if (editingOdpId) {
        await axios.put(`${API}/ftth/odps/${editingOdpId}`, payload, { headers: headersValue });
        toast.success('ODP berhasil diperbarui');
      } else {
        await axios.post(`${API}/ftth/odps`, payload, { headers: headersValue });
        toast.success('ODP berhasil ditambahkan');
      }

      resetOdpForm();
      fetchTopology();
    } catch (error) {
      handleApiError(error, editingOdpId ? 'Gagal memperbarui ODP' : 'Gagal menambah ODP');
    }
  };

  const handleDeleteOlt = async (id) => {
    if (!window.confirm('Hapus OLT ini? ODP yang terkait akan dilepas dari OLT.')) return;
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      await axios.delete(`${API}/ftth/olts/${id}`, { headers: headersValue });
      if (editingOltId === id) resetOltForm();
      toast.success('OLT dihapus');
      fetchTopology();
    } catch (error) {
      handleApiError(error, 'Gagal menghapus OLT');
    }
  };

  const handleDeleteOdp = async (id) => {
    if (!window.confirm('Hapus ODP ini?')) return;
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      await axios.delete(`${API}/ftth/odps/${id}`, { headers: headersValue });
      if (editingOdpId === id) resetOdpForm();
      toast.success('ODP dihapus');
      fetchTopology();
    } catch (error) {
      handleApiError(error, 'Gagal menghapus ODP');
    }
  };

  const handleRetryNow = () => {
    clearFetchRetry();
    fetchTopology();
  };

  const allOltsById = useMemo(() => {
    const mapped = {};
    olts.forEach((olt) => {
      if (olt?.id) mapped[olt.id] = olt;
    });
    return mapped;
  }, [olts]);

  const nasByRouterId = useMemo(() => {
    const mapped = {};
    nasRouters.forEach((item) => {
      const key = String(item?.router_id || item?.id || '').trim();
      if (!key) return;
      mapped[key] = item;
    });
    return mapped;
  }, [nasRouters]);

  const filteredOlts = useMemo(() => {
    return olts.filter((olt) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(olt.kriteria) !== infraFilter) return false;
      if (selectedNasId !== 'all' && String(olt.router_id || '') !== String(selectedNasId)) return false;
      if (selectedOltId !== 'all' && String(olt.id) !== String(selectedOltId)) return false;
      return true;
    });
  }, [infraFilter, olts, selectedNasId, selectedOltId]);

  const filteredOdps = useMemo(() => {
    return odps.filter((odp) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(odp.kriteria) !== infraFilter) return false;
      const linkedOlt = allOltsById[odp.olt_id];
      const linkedRouterId = String(odp.router_id || linkedOlt?.router_id || '').trim();
      if (selectedNasId !== 'all' && linkedRouterId !== String(selectedNasId)) return false;
      if (selectedOltId !== 'all' && String(odp.olt_id || '') !== String(selectedOltId)) return false;
      return true;
    });
  }, [allOltsById, infraFilter, odps, selectedNasId, selectedOltId]);

  const filteredOltsById = useMemo(() => {
    const mapped = {};
    filteredOlts.forEach((olt) => {
      if (olt?.id) mapped[olt.id] = olt;
    });
    return mapped;
  }, [filteredOlts]);

  const filteredOdpsById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      if (odp?.id) mapped[odp.id] = odp;
    });
    return mapped;
  }, [filteredOdps]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const rawContext = String(params.get('context') || '').trim().toLowerCase();
    const rawMode = String(params.get('mode') || '').trim().toLowerCase();
    const rawPlanningAction = String(params.get('planningAction') || '').trim().toLowerCase();
    const rawSourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const rawSourceId = String(params.get('sourceId') || '').trim();
    const signature = `${rawContext}|${rawMode}|${rawPlanningAction}|${rawSourceType}|${rawSourceId}`;
    if (!rawContext && !rawMode && !rawPlanningAction && !rawSourceType && !rawSourceId) return;
    if (deepLinkHandledRef.current === signature) return;
    deepLinkHandledRef.current = signature;

    if (rawMode === 'live') setFtthContext('live');
    if (rawMode === 'plan' || rawContext === 'planning') setFtthContext('planning');
    if (rawPlanningAction === 'ai-odp' || rawPlanningAction === 'odp' || rawPlanningAction === 'olt') {
      if (rawPlanningAction === 'ai-odp' && rawContext !== 'live') {
        setFtthContext('planning');
      }
      setPlanningAction(rawPlanningAction);
      setInfraFlowTab(rawPlanningAction === 'olt' ? 'olt' : 'odp');
      setSidePanelTab(rawPlanningAction === 'ai-odp' ? 'capacity' : 'draft');
    }

    if (rawSourceType === 'nas' && rawSourceId) {
      setSelectedNasId(rawSourceId);
      setSidePanelTab('capacity');
      return;
    }

    if (rawSourceType === 'olt' && rawSourceId) {
      setSelectedOltId(rawSourceId);
      setFocusedNode({ type: 'olt', id: rawSourceId });
      setSidePanelTab('asset');
      return;
    }

    if (rawSourceType === 'odp' && rawSourceId) {
      const linkedOdp = odps.find((item) => String(item?.id || '') === rawSourceId) || null;
      if (linkedOdp?.router_id) setSelectedNasId(String(linkedOdp.router_id));
      if (linkedOdp?.olt_id) setSelectedOltId(String(linkedOdp.olt_id));
      setFocusedNode({ type: 'odp', id: rawSourceId });
      setSidePanelTab(rawPlanningAction === 'ai-odp' ? 'capacity' : 'asset');
    }
  }, [location.search, odps]);
  const odpsById = useMemo(() => {
    const mapped = {};
    odps.forEach((odp) => {
      if (odp?.id) mapped[odp.id] = odp;
    });
    return mapped;
  }, [odps]);

  useEffect(() => {
    if (selectedOltId === 'all') return;
    if (!filteredOlts.some((olt) => String(olt.id) === String(selectedOltId))) {
      setSelectedOltId('all');
    }
  }, [filteredOlts, selectedOltId]);

  useEffect(() => {
    if (selectedNasId === 'all') return;
    if (!nasByRouterId[String(selectedNasId)]) {
      setSelectedNasId('all');
    }
  }, [nasByRouterId, selectedNasId]);

  const nasOptions = useMemo(() => {
    return Object.entries(nasByRouterId)
      .map(([routerId, item]) => ({
        router_id: routerId,
        name: String(item?.name || item?.nas_identifier || routerId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [nasByRouterId]);

  useEffect(() => {
    setNasLookupItems(nasOptions);
  }, [nasOptions]);

  const nasComboOptions = useMemo(() => {
    const source = nasLookupItems.length > 0 ? nasLookupItems : nasOptions;
    return source.map((item) => ({
      value: String(item.router_id),
      label: String(item.name),
      keywords: String(item.name),
    }));
  }, [nasLookupItems, nasOptions]);

  const oltFilterComboOptions = useMemo(() => {
    return filteredOlts.map((item) => ({
      value: String(item.id),
      label: item.code ? `${item.code} - ${item.name}` : String(item.name || item.id || ''),
      description: String(item.location || ''),
      keywords: `${item.code || ''} ${item.name || ''} ${item.location || ''}`.trim(),
    }));
  }, [filteredOlts]);

  const oltOptionsForOdpForm = useMemo(() => {
    const source = oltLookupItems.length > 0 ? oltLookupItems : olts;
    return source.filter((olt) => {
      if (!String(odpForm.router_id || '').trim()) return true;
      return String(olt.router_id || '').trim() === String(odpForm.router_id || '').trim();
    });
  }, [olts, oltLookupItems, odpForm.router_id]);

  const oltOptionsForOdpFormCombo = useMemo(() => {
    return oltOptionsForOdpForm.map((item) => ({
      value: String(item.id),
      label: item.code ? `${item.code} - ${item.name}` : String(item.name || item.id || ''),
      description: String(item.location || ''),
      keywords: `${item.code || ''} ${item.name || ''}`.trim(),
    }));
  }, [oltOptionsForOdpForm]);

  const overlapNodePositionLookup = useMemo(() => {
    const infraNodes = [
      ...filteredOlts
        .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
        .map((item) => ({ id: `olt:${item.id}`, latitude: Number(item.latitude), longitude: Number(item.longitude) })),
      ...filteredOdps
        .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
        .map((item) => ({ id: `odp:${item.id}`, latitude: Number(item.latitude), longitude: Number(item.longitude) })),
    ];
    return buildOverlapPositionLookup(infraNodes);
  }, [filteredOlts, filteredOdps]);

  const getInfraDisplayPosition = useCallback((type, node) => {
    const id = String(node?.id || '').trim();
    if (!id || !hasValidCoordinates(node?.latitude, node?.longitude)) return null;
    const key = `${type}:${id}`;
    return overlapNodePositionLookup[key] || [Number(node.latitude), Number(node.longitude)];
  }, [overlapNodePositionLookup]);

  const customersForMap = useMemo(() => {
    return customers.filter((customer) => {
      const odpId = customer.linked_odp_id || customer.odp_id || null;
      const linkedOdp = odpId ? filteredOdpsById[odpId] : null;
      const linkedOltId = customer.linked_olt_id || customer.olt_id || (odpId && odpsById[odpId] ? odpsById[odpId].olt_id : null) || null;

      if (selectedOltId !== 'all' && String(linkedOltId || '') !== String(selectedOltId)) return false;
      if (infraFilter !== 'all' && !linkedOdp) return false;
      return true;
    });
  }, [customers, filteredOdpsById, odpsById, infraFilter, selectedOltId]);

  const linkedCustomersForView = useMemo(() => customersForMap.filter((c) => Boolean(c.linked_odp_id || c.odp_id)), [customersForMap]);

  const unlinkedCustomersForView = useMemo(() => customersForMap.filter((c) => !c.linked_odp_id && !c.odp_id), [customersForMap]);

  const customerPointsForMap = useMemo(() => {
    return customersForMap.filter((customer) => hasValidCoordinates(customer.latitude, customer.longitude));
  }, [customersForMap]);

  const customerCountByOdpId = useMemo(() => {
    const mapped = {};
    customerPointsForMap.forEach((customer) => {
      const odpId = String(customer.linked_odp_id || customer.odp_id || '').trim();
      if (!odpId) return;
      mapped[odpId] = (mapped[odpId] || 0) + 1;
    });
    return mapped;
  }, [customerPointsForMap]);

  const aiNodeInsights = useMemo(() => {
    const byId = {};
    let totalAnomaly = 0;
    let latencyHigh = 0;
    let signalWeak = 0;
    let offline = 0;
    let predictiveCount = 0;
    customerPointsForMap.forEach((customer) => {
      const anomaly = detectAnomaly(customer);
      const predictions = predictIssue(customer);
      if (anomaly.hasAnomaly) totalAnomaly += 1;
      if (anomaly.issues.some((item) => item.code === 'latency_high')) latencyHigh += 1;
      if (anomaly.issues.some((item) => item.code === 'signal_weak')) signalWeak += 1;
      if (anomaly.issues.some((item) => item.code === 'offline')) offline += 1;
      if (predictions.length > 0) predictiveCount += 1;
      byId[String(customer.id || '')] = { anomaly, predictions };
    });
    return { byId, summary: { totalAnomaly, latencyHigh, signalWeak, offline, predictiveCount } };
  }, [customerPointsForMap]);

  const anomalyByOdpId = useMemo(() => {
    const mapped = {};
    customerPointsForMap.forEach((customer) => {
      const odpId = String(customer.linked_odp_id || customer.odp_id || '').trim();
      if (!odpId) return;
      const insight = aiNodeInsights.byId[String(customer.id || '')];
      if (insight?.anomaly?.hasAnomaly) mapped[odpId] = true;
    });
    return mapped;
  }, [customerPointsForMap, aiNodeInsights.byId]);

  const odpRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const odpId = String(odp?.id || '').trim();
      if (odpId) mapped[odpId] = 'unknown';
    });
    customerPointsForMap.forEach((customer) => {
      const odpId = String(customer.linked_odp_id || customer.odp_id || '').trim();
      if (!odpId) return;
      const state = String(customer.status || customer.connection_status || '').toLowerCase();
      if (state === 'online') {
        mapped[odpId] = 'online';
      } else if (mapped[odpId] !== 'online') {
        mapped[odpId] = state === 'offline' ? 'offline' : 'unknown';
      }
    });
    return mapped;
  }, [customerPointsForMap, filteredOdps]);

  const oltCustomerCountById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const oltId = String(odp?.olt_id || '').trim();
      const odpId = String(odp?.id || '').trim();
      if (!oltId || !odpId) return;
      mapped[oltId] = (mapped[oltId] || 0) + Number(customerCountByOdpId[odpId] || 0);
    });
    return mapped;
  }, [customerCountByOdpId, filteredOdps]);

  const oltRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOlts.forEach((olt) => {
      const oltId = String(olt?.id || '').trim();
      if (oltId) mapped[oltId] = 'unknown';
    });
    filteredOdps.forEach((odp) => {
      const oltId = String(odp?.olt_id || '').trim();
      const odpId = String(odp?.id || '').trim();
      if (!oltId || !odpId) return;
      const odpState = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
      if (odpState === 'online') {
        mapped[oltId] = 'online';
      } else if (mapped[oltId] !== 'online' && odpState === 'offline') {
        mapped[oltId] = 'offline';
      }
    });
    return mapped;
  }, [filteredOlts, filteredOdps, odpRuntimeStatusById]);

  const anomalyByOltId = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const odpId = String(odp?.id || '').trim();
      const oltId = String(odp?.olt_id || '').trim();
      if (!odpId || !oltId) return;
      if (anomalyByOdpId[odpId]) mapped[oltId] = true;
    });
    return mapped;
  }, [filteredOdps, anomalyByOdpId]);

  const mapPoints = useMemo(() => {
    const points = [];

    filteredOlts.forEach((olt) => {
      if (hasValidCoordinates(olt.latitude, olt.longitude)) {
        points.push([Number(olt.latitude), Number(olt.longitude)]);
      }
    });

    filteredOdps.forEach((odp) => {
      if (hasValidCoordinates(odp.latitude, odp.longitude)) {
        points.push([Number(odp.latitude), Number(odp.longitude)]);
      }
    });

    customerPointsForMap.forEach((customer) => {
      points.push([Number(customer.latitude), Number(customer.longitude)]);
    });

    return points;
  }, [filteredOlts, filteredOdps, customerPointsForMap]);

  const activeContextCategory = ftthContext === 'planning' ? 'plan' : 'live';

  const contextOlts = useMemo(() => {
    if (showCompareLayer) return filteredOlts;
    return filteredOlts.filter((item) => normalizeInfraKriteria(item?.kriteria) === activeContextCategory);
  }, [activeContextCategory, filteredOlts, showCompareLayer]);

  const contextOltsById = useMemo(() => {
    const mapped = {};
    contextOlts.forEach((item) => {
      const id = String(item?.id || '').trim();
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [contextOlts]);

  const contextOdps = useMemo(() => {
    if (showCompareLayer) return filteredOdps;
    return filteredOdps.filter((item) => normalizeInfraKriteria(item?.kriteria) === activeContextCategory);
  }, [activeContextCategory, filteredOdps, showCompareLayer]);

  const contextOdpsById = useMemo(() => {
    const mapped = {};
    contextOdps.forEach((item) => {
      const id = String(item?.id || '').trim();
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [contextOdps]);

  const contextCustomersForMap = useMemo(() => {
    if (showCompareLayer) return customerPointsForMap;
    return customerPointsForMap.filter((customer) => {
      const odpId = String(customer?.linked_odp_id || customer?.odp_id || '').trim();
      if (!odpId) return ftthContext === 'live';
      return Boolean(contextOdpsById[odpId]);
    });
  }, [contextOdpsById, customerPointsForMap, ftthContext, showCompareLayer]);

  const capacityPrediction = useMemo(() => {
    const odpPredictions = buildOdpCapacityPredictions({
      odps: contextOdps,
      customers,
      resolveCustomerOdpId: (customer) => String(customer?.linked_odp_id || customer?.odp_id || '').trim(),
    });
    const nasPredictions = buildNasCapacityPredictions({
      nasRouters: nasOptions,
      customers,
      resolveCustomerRouterId: (customer) => {
        const direct = String(customer?.linked_router_id || customer?.router_id || '').trim();
        if (direct) return direct;
        const linkedOdpId = String(customer?.linked_odp_id || customer?.odp_id || '').trim();
        if (linkedOdpId && odpsById[linkedOdpId]) {
          const odp = odpsById[linkedOdpId];
          const linkedOlt = allOltsById[String(odp?.olt_id || '').trim()];
          return String(odp?.router_id || linkedOlt?.router_id || '').trim();
        }
        return '';
      },
    });
    const byOdpId = {};
    odpPredictions.forEach((item) => {
      byOdpId[String(item.id)] = item;
    });
    return {
      odps: odpPredictions,
      nas: nasPredictions,
      byOdpId,
      insights: buildCapacityInsights({ odpPredictions, nasPredictions }),
    };
  }, [allOltsById, contextOdps, customers, nasOptions, odpsById]);
  const capacityRecommendations = useMemo(() => {
    return buildCapacityRecommendations({
      odpPredictions: capacityPrediction.odps,
      nasPredictions: capacityPrediction.nas,
      strategy: aiPlanningStrategy,
      limit: 6,
    });
  }, [aiPlanningStrategy, capacityPrediction.nas, capacityPrediction.odps]);

  const workspaceMapPoints = useMemo(() => {
    const points = [];
    contextOlts.forEach((olt) => {
      if (hasValidCoordinates(olt.latitude, olt.longitude)) {
        points.push([Number(olt.latitude), Number(olt.longitude)]);
      }
    });
    contextOdps.forEach((odp) => {
      if (hasValidCoordinates(odp.latitude, odp.longitude)) {
        points.push([Number(odp.latitude), Number(odp.longitude)]);
      }
    });
    contextCustomersForMap.forEach((customer) => {
      if (hasValidCoordinates(customer.latitude, customer.longitude)) {
        points.push([Number(customer.latitude), Number(customer.longitude)]);
      }
    });
    return points;
  }, [contextCustomersForMap, contextOdps, contextOlts]);

  const planningUpstreamOlts = useMemo(() => {
    if (contextOlts.length > 0) return contextOlts;
    return filteredOlts.filter((item) => normalizeInfraKriteria(item?.kriteria) === 'live');
  }, [contextOlts, filteredOlts]);

  const planningAnchorPoint = useMemo(() => {
    if (selectedOltId !== 'all') {
      const selectedOlt = allOltsById[selectedOltId];
      if (selectedOlt && hasValidCoordinates(selectedOlt.latitude, selectedOlt.longitude)) {
        return {
          id: String(selectedOlt.id),
          type: 'olt',
          name: selectedOlt.name || 'OLT',
          point: [Number(selectedOlt.latitude), Number(selectedOlt.longitude)],
        };
      }
    }

    const odpCandidates = filteredOdps
      .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
      .sort((a, b) => {
        const aid = Number(a?.id);
        const bid = Number(b?.id);
        if (Number.isFinite(aid) && Number.isFinite(bid)) return bid - aid;
        return String(b?.id || '').localeCompare(String(a?.id || ''));
      });
    if (odpCandidates.length > 0) {
      const lastOdp = odpCandidates[0];
      return {
        id: String(lastOdp.id),
        type: 'odp',
        name: lastOdp.name || 'ODP',
        point: [Number(lastOdp.latitude), Number(lastOdp.longitude)],
      };
    }

    const oltCandidates = planningUpstreamOlts.filter((item) => hasValidCoordinates(item.latitude, item.longitude));
    if (oltCandidates.length > 0) {
      const nearest = oltCandidates[0];
      return {
        id: String(nearest.id),
        type: 'olt',
        name: nearest.name || 'OLT',
        point: [Number(nearest.latitude), Number(nearest.longitude)],
      };
    }
    return null;
  }, [allOltsById, filteredOdps, planningUpstreamOlts, selectedOltId]);

  const uncoveredPlanningCustomers = useMemo(() => {
    const planningOdpIds = new Set(contextOdps.map((item) => String(item.id || '')));
    return customerPointsForMap.filter((customer) => {
      const linkedOdpId = String(customer?.linked_odp_id || customer?.odp_id || '').trim();
      if (!linkedOdpId) return true;
      if (planningOdpIds.has(linkedOdpId)) return false;
      const linkedOdp = odpsById[linkedOdpId];
      if (!linkedOdp) return true;
      return normalizeInfraKriteria(linkedOdp?.kriteria) !== 'plan';
    });
  }, [contextOdps, customerPointsForMap, odpsById]);

  const planningNodesForFit = useMemo(() => {
    if (planningAction !== 'ai-odp' || aiPlanningCandidates.length === 0) return [];
    return aiPlanningCandidates.map((candidate) => [candidate.latitude, candidate.longitude]);
  }, [aiPlanningCandidates, planningAction]);

  const mapWorkspacePointsWithAi = useMemo(() => {
    if (planningNodesForFit.length === 0) return workspaceMapPoints;
    return [...workspaceMapPoints, ...planningNodesForFit];
  }, [planningNodesForFit, workspaceMapPoints]);

  const getAllowedCapacities = useCallback(() => {
    const values = ODP_CAPACITY_OPTIONS.filter((item) => Boolean(aiAllowedCapacity[item]));
    return values.length > 0 ? values : [...ODP_CAPACITY_OPTIONS];
  }, [aiAllowedCapacity]);

  const resolveSuggestedCapacity = useCallback((houseCount, strategy, allowedCapacities) => {
    const sortedAllowed = [...allowedCapacities].sort((a, b) => a - b);
    const demand = Number(houseCount || 0);
    if (sortedAllowed.length === 1) return sortedAllowed[0];

    if (strategy === 'cost') {
      const conservative = sortedAllowed.find((item) => item >= Math.max(4, demand));
      return conservative || sortedAllowed[sortedAllowed.length - 1];
    }
    if (strategy === 'growth') {
      if (demand >= 15 && sortedAllowed.includes(24)) return 24;
      if (demand >= 9 && sortedAllowed.includes(16)) return 16;
      const aggressive = sortedAllowed.find((item) => item >= Math.max(8, demand + 4));
      return aggressive || sortedAllowed[sortedAllowed.length - 1];
    }
    if (demand <= 7 && sortedAllowed.includes(8)) return 8;
    if (demand <= 13 && sortedAllowed.includes(16)) return 16;
    const balanced = sortedAllowed.find((item) => item >= demand);
    return balanced || sortedAllowed[sortedAllowed.length - 1];
  }, []);

  const describeCapacityReason = useCallback((capacity, housesCovered, strategy) => {
    if (capacity === 8) {
      return strategy === 'cost'
        ? 'ODP 8 dipilih untuk rollout hemat biaya pada klaster kecil.'
        : 'ODP 8 cocok untuk klaster kecil dengan overlap rendah.';
    }
    if (capacity === 16) {
      return strategy === 'growth'
        ? 'ODP 16 menjaga ruang pertumbuhan tanpa over-capacity berlebihan.'
        : 'ODP 16 seimbang untuk klaster menengah dan utilisasi stabil.';
    }
    if (capacity === 24) {
      return housesCovered >= 18
        ? 'ODP 24 direkomendasikan karena klaster padat dan potensi ekspansi tinggi.'
        : 'ODP 24 dipilih sebagai strategi growth-ready di area prioritas.';
    }
    return 'Kapasitas dipilih berdasarkan kepadatan, cakupan, dan strategi.';
  }, []);

  const scoreCandidate = useCallback((candidate, strategy) => {
    const strategyWeights = strategy === 'cost'
      ? { distance: 0.34, density: 0.18, coverage: 0.22, overlap: 0.16, capacityFit: 0.1 }
      : strategy === 'growth'
        ? { distance: 0.16, density: 0.24, coverage: 0.26, overlap: 0.12, capacityFit: 0.22 }
        : { distance: 0.24, density: 0.22, coverage: 0.24, overlap: 0.14, capacityFit: 0.16 };

    const totalScore = (
      (candidate.distanceScore * strategyWeights.distance)
      + (candidate.densityScore * strategyWeights.density)
      + (candidate.coverageScore * strategyWeights.coverage)
      + (candidate.overlapScore * strategyWeights.overlap)
      + (candidate.capacityFitScore * strategyWeights.capacityFit)
    );
    return Math.round(clamp(totalScore, 0, 100));
  }, []);

  const handleRunAiPlanning = useCallback(() => {
    const warnings = [];
    const allowedCapacities = getAllowedCapacities();
    const anchor = planningAnchorPoint;
    if (!anchor) {
      toast.error('Titik upstream belum tersedia. Pilih OLT atau lengkapi koordinat OLT/ODP.');
      return;
    }
    if (uncoveredPlanningCustomers.length === 0) {
      warnings.push('Tidak ada rumah belum tercover di scope filter saat ini.');
    }

    const sourcePoints = uncoveredPlanningCustomers.length > 0 ? uncoveredPlanningCustomers : contextCustomersForMap;
    const clusters = [];
    const radius = Math.max(80, Number(aiPlanningRadius || 220));
    sourcePoints.forEach((item) => {
      if (!hasValidCoordinates(item.latitude, item.longitude)) return;
      const point = [Number(item.latitude), Number(item.longitude)];
      let linkedCluster = null;
      for (let i = 0; i < clusters.length; i += 1) {
        const cluster = clusters[i];
        if (distanceMeters(cluster.center, point) <= radius) {
          linkedCluster = cluster;
          break;
        }
      }
      if (!linkedCluster) {
        linkedCluster = {
          id: `cluster-${clusters.length + 1}`,
          points: [],
          customerIds: [],
          center: point,
        };
        clusters.push(linkedCluster);
      }
      linkedCluster.points.push(point);
      linkedCluster.customerIds.push(String(item.id || item.customer_id || `${point[0]}-${point[1]}`));
      const sumLat = linkedCluster.points.reduce((acc, current) => acc + Number(current[0]), 0);
      const sumLng = linkedCluster.points.reduce((acc, current) => acc + Number(current[1]), 0);
      linkedCluster.center = [sumLat / linkedCluster.points.length, sumLng / linkedCluster.points.length];
    });

    let selectedClusters = clusters;
    if (aiPlanningUseCase === 'next-point') {
      selectedClusters = [...clusters]
        .sort((a, b) => distanceMeters(anchor.point, a.center) - distanceMeters(anchor.point, b.center))
        .slice(0, AI_PLANNING_MAX_CANDIDATES);
    } else {
      selectedClusters = [...clusters]
        .sort((a, b) => b.points.length - a.points.length)
        .slice(0, AI_PLANNING_MAX_CANDIDATES);
    }

    if (selectedClusters.length === 0 && anchor?.point) {
      selectedClusters = [{
        id: 'cluster-fallback',
        points: [anchor.point],
        customerIds: [],
        center: anchor.point,
      }];
      warnings.push('Belum ada klaster rumah valid, kandidat dibuat dari anchor saat ini.');
    }

    const maxDensity = Math.max(1, ...selectedClusters.map((cluster) => cluster.points.length));
    const candidates = selectedClusters.map((cluster, index) => {
      const candidatePoint = cluster.center;
      const distanceFromAnchor = distanceMeters(anchor.point, candidatePoint);
      const nearbyExisting = contextOdps.filter((odp) => hasValidCoordinates(odp.latitude, odp.longitude))
        .map((odp) => distanceMeters(candidatePoint, [Number(odp.latitude), Number(odp.longitude)]));
      const nearestExistingDistance = nearbyExisting.length > 0 ? Math.min(...nearbyExisting) : Number.POSITIVE_INFINITY;
      const overlapRatio = Number.isFinite(nearestExistingDistance)
        ? clamp((radius - nearestExistingDistance) / radius, 0, 1)
        : 0;
      const housesCovered = cluster.points.length;
      const distanceScore = Math.round(clamp(100 - (distanceFromAnchor / Math.max(radius * 3, 220)) * 100, 0, 100));
      const densityScore = Math.round(clamp((housesCovered / maxDensity) * 100, 0, 100));
      const coverageScore = Math.round(clamp((housesCovered / Math.max(1, sourcePoints.length)) * 260, 0, 100));
      const overlapScore = Math.round(clamp((1 - overlapRatio) * 100, 0, 100));
      const suggestedCapacity = resolveSuggestedCapacity(housesCovered, aiPlanningStrategy, allowedCapacities);
      const expectedLoadRatio = housesCovered / Math.max(1, suggestedCapacity);
      const capacityFitScore = Math.round(clamp(100 - Math.abs(expectedLoadRatio - 0.82) * 90, 0, 100));
      const base = {
        id: `ai-candidate-${index + 1}`,
        rank: String.fromCharCode(65 + index),
        label: `Kandidat ${String.fromCharCode(65 + index)}`,
        latitude: Number(candidatePoint[0]),
        longitude: Number(candidatePoint[1]),
        housesCovered,
        distanceFromAnchor: Math.round(distanceFromAnchor),
        nearestExistingDistance: Number.isFinite(nearestExistingDistance) ? Math.round(nearestExistingDistance) : null,
        overlapRatio,
        distanceScore,
        densityScore,
        coverageScore,
        overlapScore,
        capacityFitScore,
        suggestedCapacity,
        servedCustomerIds: cluster.customerIds,
        warnings: [],
        strategy: aiPlanningStrategy,
      };
      const totalScore = scoreCandidate(base, aiPlanningStrategy);
      const reasons = [
        `${aiPlanningUseCase === 'next-point' ? 'Ekspansi dari titik terakhir' : 'Klaster kepadatan'} dengan estimasi ${housesCovered} rumah.`,
        `Jarak dari ${anchor.name}: ${Math.round(distanceFromAnchor)} m.`,
        describeCapacityReason(suggestedCapacity, housesCovered, aiPlanningStrategy),
      ];
      if (overlapRatio > 0.45) {
        base.warnings.push('Overlap cukup tinggi dengan cakupan ODP existing.');
      }
      if (distanceFromAnchor > radius * 2.8) {
        base.warnings.push('Jarak dari upstream cukup jauh, pertimbangkan titik perantara.');
      }
      if (suggestedCapacity === 8 && housesCovered >= 14) {
        base.warnings.push('Cluster besar, ODP 8 berisiko cepat penuh.');
      }
      if (suggestedCapacity === 24 && housesCovered <= 7) {
        base.warnings.push('Potensi underutilization untuk ODP 24 pada cluster kecil.');
      }
      return {
        ...base,
        totalScore,
        reasons,
        anchorId: anchor.id,
        anchorType: anchor.type,
      };
    }).sort((a, b) => b.totalScore - a.totalScore);

    setAiPlanningCandidates(candidates);
    setAiPlanningSelectedCandidateId(candidates[0]?.id || '');
    setAiPlanningComparedIds(candidates.length > 1 ? [candidates[0]?.id, candidates[1]?.id].filter(Boolean) : []);
    setAiPlanningWarnings(warnings);
    setAiPlanningLastRunAt(new Date().toISOString());
    setSidePanelTab('draft');
    toast.success(`AI Planning selesai: ${candidates.length} kandidat dianalisis.`);
  }, [
    aiPlanningRadius,
    aiPlanningStrategy,
    aiPlanningUseCase,
    contextCustomersForMap,
    contextOdps,
    describeCapacityReason,
    getAllowedCapacities,
    planningAnchorPoint,
    resolveSuggestedCapacity,
    scoreCandidate,
    uncoveredPlanningCustomers,
  ]);

  const aiPlanningSelectedCandidate = useMemo(
    () => aiPlanningCandidates.find((item) => item.id === aiPlanningSelectedCandidateId) || null,
    [aiPlanningCandidates, aiPlanningSelectedCandidateId],
  );

  const aiPlanningComparedCandidates = useMemo(
    () => aiPlanningCandidates.filter((item) => aiPlanningComparedIds.includes(item.id)).slice(0, 3),
    [aiPlanningCandidates, aiPlanningComparedIds],
  );

  const handleToggleCandidateCompare = useCallback((candidateId) => {
    setAiPlanningComparedIds((prev) => {
      if (prev.includes(candidateId)) {
        return prev.filter((id) => id !== candidateId);
      }
      return [...prev, candidateId].slice(-3);
    });
  }, []);

  const handleApplyAiCandidate = useCallback((candidate) => {
    if (!candidate) return;
    const nearestOlt = planningUpstreamOlts
      .filter((olt) => hasValidCoordinates(olt.latitude, olt.longitude))
      .map((olt) => ({
        ...olt,
        distance: distanceMeters([candidate.latitude, candidate.longitude], [Number(olt.latitude), Number(olt.longitude)]),
      }))
      .sort((a, b) => a.distance - b.distance)[0];

    setFtthContext('planning');
    setPlanningAction('odp');
    setInfraFlowTab('odp');
    setSidePanelTab('draft');
    setOdpStep(2);
    setOdpForm((prev) => ({
      ...prev,
      name: prev.name || `ODP-PLAN-${candidate.rank}`,
      kriteria: 'plan',
      latitude: Number(candidate.latitude).toFixed(6),
      longitude: Number(candidate.longitude).toFixed(6),
      suggested_capacity: String(candidate.suggestedCapacity || DEFAULT_ODP_CAPACITY),
      olt_id: prev.olt_id || (nearestOlt ? String(nearestOlt.id) : ''),
      router_id: prev.router_id || (nearestOlt?.router_id ? String(nearestOlt.router_id) : prev.router_id),
    }));
    setAiPlanningSelectedCandidateId(candidate.id);
    if (candidate.warnings?.length) {
      toast.warning(`Kandidat ${candidate.rank} diterapkan dengan ${candidate.warnings.length} warning.`);
    } else {
      toast.success(`Kandidat ${candidate.rank} diterapkan ke draft ODP.`);
    }
  }, [planningUpstreamOlts]);

  const handleSimulateAiCandidate = useCallback((candidate) => {
    if (!candidate) return;
    setAiPlanningSelectedCandidateId(candidate.id);
    setFocusedNode({ type: 'candidate', id: candidate.id });
    toast.message(`Simulasi kandidat ${candidate.rank}: estimasi ${candidate.housesCovered} rumah.`);
  }, []);

  const explorerKeyword = String(explorerQuery || '').trim().toLowerCase();

  const getCustomerSeverity = useCallback((customer) => {
    const status = String(customer?.status || '').toLowerCase();
    if (status === 'offline') return 3;
    if (status === 'not_mapped') return 3;
    const attenuation = Number(customer?.attenuation_db);
    if (Number.isFinite(attenuation)) {
      if (attenuation > 25) return 3;
      if (attenuation >= 20) return 2;
    }
    if (status === 'online') return 1;
    return 0;
  }, []);

  const odpCustomerCounts = useMemo(() => {
    const counts = {};
    customers.forEach((customer) => {
      const odpId = customer.linked_odp_id || customer.odp_id;
      if (odpId) counts[odpId] = (counts[odpId] || 0) + 1;
    });
    return counts;
  }, [customers]);

  const explorerOlts = useMemo(() => {
    return filteredOlts
      .filter((olt) => {
        if (!explorerKeyword) return true;
        const haystack = [olt.name, olt.code, olt.location].map((item) => String(item || '').toLowerCase()).join(' ');
        return haystack.includes(explorerKeyword);
      })
      .map((olt) => ({
        ...olt,
        odp_count: filteredOdps.filter((odp) => odp.olt_id === olt.id).length,
      }));
  }, [explorerKeyword, filteredOdps, filteredOlts]);

  const explorerOltOptions = useMemo(
    () => explorerOlts.map((olt) => ({ value: String(olt.id), label: `${olt.name} (${olt.odp_count} ODP)` })),
    [explorerOlts],
  );

  const explorerOdps = useMemo(() => {
    return filteredOdps
      .filter((odp) => {
        if (explorerOltId !== 'all' && String(odp.olt_id || '') !== String(explorerOltId)) return false;
        if (!explorerKeyword) return true;
        const haystack = [odp.name, odp.code, odp.address, filteredOltsById[odp.olt_id]?.name].map((item) => String(item || '').toLowerCase()).join(' ');
        return haystack.includes(explorerKeyword);
      })
      .map((odp) => ({
        ...odp,
        customer_count: odpCustomerCounts[odp.id] || 0,
        olt_name: filteredOltsById[odp.olt_id]?.name || '-',
      }));
  }, [explorerKeyword, filteredOdps, filteredOltsById, odpCustomerCounts, explorerOltId]);

  const explorerOdpOptions = useMemo(
    () => explorerOdps.map((odp) => ({ value: String(odp.id), label: `${odp.name} (${odp.customer_count}/${DEFAULT_ODP_CAPACITY})` })),
    [explorerOdps],
  );

  const explorerCustomers = useMemo(() => {
    return customers
      .filter((customer) => {
        const odpId = customer.linked_odp_id || customer.odp_id;
        const linkedOdp = odpId ? filteredOdpsById[odpId] : null;
        if (explorerOltId !== 'all') {
          const linkedOltId = linkedOdp?.olt_id || customer.linked_olt_id || customer.olt_id || '';
          if (String(linkedOltId) !== String(explorerOltId)) return false;
        }
        if (explorerOdpId !== 'all' && String(odpId || '') !== String(explorerOdpId)) return false;
        if (!explorerKeyword) return true;
        const haystack = [customer.name, customer.customer_id, customer.odp, filteredOdpsById[customer.odp_id]?.name].map((item) => String(item || '').toLowerCase()).join(' ');
        return haystack.includes(explorerKeyword);
      })
      .map((customer) => ({
        ...customer,
        linked_odp_name: filteredOdpsById[customer.linked_odp_id || customer.odp_id]?.name || customer.odp || '-',
        severity: getCustomerSeverity(customer),
      }))
      .filter((customer) => (explorerIssueOnly ? Number(customer.severity || 0) > 0 : true))
      .sort((a, b) => {
        const severityDiff = Number(b.severity || 0) - Number(a.severity || 0);
        if (severityDiff !== 0) return severityDiff;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  }, [customers, explorerIssueOnly, explorerKeyword, filteredOdpsById, explorerOltId, explorerOdpId, getCustomerSeverity]);

  const explorerCustomersVisible = useMemo(
    () => explorerCustomers.slice(0, explorerCustomerLimit),
    [explorerCustomers, explorerCustomerLimit],
  );

  useEffect(() => {
    if (explorerOltId === 'all') return;
    const exists = explorerOlts.some((olt) => String(olt.id) === String(explorerOltId));
    if (!exists) setExplorerOltId('all');
  }, [explorerOlts, explorerOltId]);

  useEffect(() => {
    if (explorerOdpId === 'all') return;
    const exists = explorerOdps.some((odp) => String(odp.id) === String(explorerOdpId));
    if (!exists) setExplorerOdpId('all');
  }, [explorerOdps, explorerOdpId]);

  useEffect(() => {
    setExplorerCustomerLimit(EXPLORER_CUSTOMER_PAGE_SIZE);
  }, [explorerIssueOnly, explorerQuery, explorerOltId, explorerOdpId, viewMode]);

  useEffect(() => {
    if (editingOltId) {
      setFtthContext('planning');
      setPlanningAction('olt');
      setSidePanelTab('draft');
      return;
    }
    if (editingOdpId) {
      setFtthContext('planning');
      setPlanningAction('odp');
      setSidePanelTab('draft');
    }
  }, [editingOdpId, editingOltId]);

  const validationIssues = useMemo(() => {
    const issues = [];

    filteredOlts.forEach((olt) => {
      if (!hasValidCoordinates(olt.latitude, olt.longitude)) {
        issues.push({ severity: 'medium', type: 'OLT', id: olt.id, name: olt.name, message: 'OLT belum memiliki koordinat valid' });
      }
    });

    filteredOdps.forEach((odp) => {
      const customerCount = odpCustomerCounts[odp.id] || 0;
      if (!odp.olt_id || !filteredOltsById[odp.olt_id]) {
        issues.push({ severity: 'high', type: 'ODP', id: odp.id, name: odp.name, message: 'ODP belum terhubung ke OLT yang valid' });
      }
      if (!hasValidCoordinates(odp.latitude, odp.longitude)) {
        issues.push({ severity: 'medium', type: 'ODP', id: odp.id, name: odp.name, message: 'ODP belum memiliki koordinat valid' });
      }
      if (customerCount > DEFAULT_ODP_CAPACITY) {
        issues.push({ severity: 'high', type: 'ODP', id: odp.id, name: odp.name, message: 'ODP melebihi kapasitas ' + DEFAULT_ODP_CAPACITY + ' pelanggan (' + customerCount + ')' });
      }
      if (customerCount === 0) {
        issues.push({ severity: 'low', type: 'ODP', id: odp.id, name: odp.name, message: 'ODP belum memiliki pelanggan terhubung' });
      }
    });

    customers.forEach((customer) => {
      const odpId = customer.linked_odp_id || customer.odp_id;
      if (!odpId || !filteredOdpsById[odpId]) {
        issues.push({ severity: 'high', type: 'Pelanggan', id: customer.id, name: customer.name, message: 'Pelanggan belum terhubung ke ODP yang valid' });
      }
      if (!hasValidCoordinates(customer.latitude, customer.longitude)) {
        issues.push({ severity: 'medium', type: 'Pelanggan', id: customer.id, name: customer.name, message: 'Pelanggan belum memiliki koordinat valid' });
      }
    });

    return issues;
  }, [customers, filteredOdps, filteredOdpsById, filteredOlts, filteredOltsById, odpCustomerCounts]);

  const validationSummary = useMemo(() => ({
    high: validationIssues.filter((item) => item.severity === 'high').length,
    medium: validationIssues.filter((item) => item.severity === 'medium').length,
    low: validationIssues.filter((item) => item.severity === 'low').length,
  }), [validationIssues]);

  const hasTopologyNodes = contextOlts.length > 0 || contextOdps.length > 0 || contextCustomersForMap.length > 0;

  const odpCoverageZones = useMemo(() => {

    const mapped = {};

    contextCustomersForMap.forEach((customer) => {

      const odpId = customer.linked_odp_id || customer.odp_id;

      const odp = odpId ? contextOdpsById[odpId] : null;

      if (!odp?.id) return;

      if (!hasValidCoordinates(odp.latitude, odp.longitude)) return;

      if (!mapped[odp.id]) {

        mapped[odp.id] = {

          id: odp.id,

          latitude: Number(odp.latitude),

          longitude: Number(odp.longitude),

          count: 0,

        };

      }

      mapped[odp.id].count += 1;

    });



    return Object.values(mapped).map((zone) => ({

      ...zone,

      radius: 130 + Math.min(zone.count * 18, 190),

    }));

  }, [contextCustomersForMap, contextOdpsById]);

  const getCustomerStroke = (status) => {
    if (status === 'online') return '#3b82f6';
    if (status === 'offline') return '#ef4444';
    return '#9ca3af';
  };
  const getConnectionLabel = (status) => {
    if (status === 'online') return 'Online';
    if (status === 'offline') return 'Offline/Loss';
    if (status === 'not_mapped') return 'Belum Mapping ONU';
    return 'Status tidak diketahui';
  };

  return (
    <div className="relative min-h-screen bg-[#050505]">
      {showLoadingOverlay && (
        <StaffPageLoadingOverlay text="Memuat data rancangan FTTH..." />
      )}
      <aside className="hidden">
        <div className="p-6 flex-1 overflow-y-auto">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img src={ktcLogo} alt="KTC Logo" className="h-10 w-auto" />
            <span className="font-outfit font-bold text-white">Admin</span>
          </Link>

          <nav className="space-y-2">
            <Link to="/dadu" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Home className="w-5 h-5" />Dashboard
            </Link>
            <Link to="/dadu/pelanggan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Users className="w-5 h-5" />Pelanggan
            </Link>
            <Link to="/dadu/ftth" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-blue/10 text-brand-blue border border-brand-blue/30">
              <Network className="w-5 h-5" />Rancangan FTTH
            </Link>
            <Link to="/dadu/monitoring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Map className="w-5 h-5" />Monitoring Koneksi
            </Link>
            <Link to="/dadu/penanganan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Wrench className="w-5 h-5" />Penanganan
            </Link>
            <Link to="/performa-teknisi" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Users className="w-5 h-5" />Performa Teknisi
            </Link>
            <Link to="/dadu/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <CreditCard className="w-5 h-5" />Billing
            </Link>
            <Link to="/dadu/voucher" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"><CreditCard className="w-5 h-5" />Voucher</Link>
            <Link to="/dadu/berita" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Newspaper className="w-5 h-5" />Berita
            </Link>
            <Link to="/dadu/kontak" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <MessageSquare className="w-5 h-5" />Pesan Masuk
            </Link>
            <Link to="/dadu/users" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Users className="w-5 h-5" />Manajemen User
            </Link>
            <Link to="/dadu/pengaturan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Settings className="w-5 h-5" />Pengaturan
            </Link>
          </nav>
        </div>

        <div className="p-6 border-t border-white/10">
          <p className="text-white font-medium text-sm mb-4">{user?.name || 'Admin'}</p>
          <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10 w-full">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>

      <header className="hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Link to="/dadu"><Button size="sm" variant="ghost" className="text-gray-400"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <span className="font-outfit font-bold text-white">{headerSettings.title || DEFAULT_FTTH_HEADER.title}</span>
          </div>
          <Button size="sm" variant="ghost" onClick={handleLogout} className="text-red-400"><LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-[1200]">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dadu" className="flex items-center gap-3">
            <img src={ktcLogo} alt="KTC Logo" className="h-9 w-auto" />
            <div>
              <p className="font-outfit font-bold text-white leading-tight">{headerSettings.title || DEFAULT_FTTH_HEADER.title}</p>
              <p className="text-[11px] text-gray-500 leading-tight">Akses: Admin</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/dadu"><Button variant="outline" className="btn-secondary"><Home className="w-4 h-4 mr-2" />Kembali</Button></Link>
            <Button onClick={fetchTopology} className="btn-secondary" variant="outline"><Map className="w-4 h-4 mr-2" />Refresh</Button>
            <Button
              type="button"
              variant={aiModeEnabled ? 'default' : 'outline'}
              className={aiModeEnabled ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setAiModeEnabled((prev) => !prev)}
            >
              AI MODE: {aiModeEnabled ? 'ON' : 'OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="w-full px-3 py-6 lg:px-8 lg:py-8 2xl:px-10 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div>
            {!editingHeader ? (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="font-outfit text-3xl font-bold text-white mb-2">{headerSettings.title || DEFAULT_FTTH_HEADER.title}</h1>
                  {isSuperadmin && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="btn-secondary h-8 px-2"
                      onClick={() => setEditingHeader(true)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-gray-400">{headerSettings.subtitle || DEFAULT_FTTH_HEADER.subtitle}</p>
              </>
            ) : (
              <div className="space-y-2 w-full max-w-2xl">
                <Input
                  value={headerDraft.title}
                  onChange={(event) => setHeaderDraft((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder="Judul Header FTTH"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
                <Input
                  value={headerDraft.subtitle}
                  onChange={(event) => setHeaderDraft((prev) => ({ ...prev, subtitle: event.target.value }))}
                  placeholder="Subjudul Header FTTH"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
                <div className="flex items-center gap-2">
                  <Button type="button" onClick={handleSaveFtthHeader} className="btn-primary text-white" disabled={savingHeader}>
                    {savingHeader ? 'Menyimpan...' : 'Simpan Header'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="btn-secondary"
                    onClick={() => {
                      setHeaderDraft({ ...headerSettings });
                      setEditingHeader(false);
                    }}
                    disabled={savingHeader}
                  >
                    Batal
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Select value={infraFilter} onValueChange={setInfraFilter}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white w-full sm:w-[220px]">
                <SelectValue placeholder="Filter Infrastruktur" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all" className="text-white">Semua Infrastruktur</SelectItem>
                <SelectItem value="live" className="text-white">Hanya Live</SelectItem>
                <SelectItem value="plan" className="text-white">Hanya Plan</SelectItem>
              </SelectContent>
            </Select>
            <div className="w-full sm:w-[250px]">
              <SearchableComboBox
                value={selectedNasId === 'all' ? '' : selectedNasId}
                onChange={(value) => setSelectedNasId(value || 'all')}
                options={nasComboOptions}
                placeholder="Filter NAS"
                searchPlaceholder="Cari NAS..."
                emptyLabel="NAS tidak ditemukan"
                includeEmpty
                emptyValueLabel="Semua NAS"
                onSearch={searchNasLookup}
                minSearchChars={2}
                searchContextKey="ftth-filter-nas"
              />
            </div>
            <div className="w-full sm:w-[250px]">
              <SearchableComboBox
                value={selectedOltId === 'all' ? '' : selectedOltId}
                onChange={(value) => setSelectedOltId(value || 'all')}
                options={oltFilterComboOptions}
                placeholder="Filter OLT"
                searchPlaceholder="Cari OLT..."
                emptyLabel="OLT tidak ditemukan"
                includeEmpty
                emptyValueLabel="Semua OLT"
                onSearch={(query) => searchOltLookup(query, selectedNasId === 'all' ? '' : selectedNasId)}
                minSearchChars={2}
                searchContextKey={`ftth-filter-olt:${selectedNasId || 'all'}`}
              />
            </div>
            <Button onClick={fetchTopology} className="btn-secondary" variant="outline">Refresh Topologi</Button>
            {isRetryScheduled && (
              <Button onClick={handleRetryNow} className="btn-secondary" variant="outline" disabled={loading}>
                Coba Lagi Sekarang
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400">Mode Sistem:</span>
            <Button type="button" variant={systemMode === 'design' ? 'default' : 'outline'} className={systemMode === 'design' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => handleSystemModeChange('design')}>Design</Button>
            <Button type="button" variant={systemMode === 'monitor' ? 'default' : 'outline'} className={systemMode === 'monitor' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => handleSystemModeChange('monitor')}>Monitor</Button>
            <Button type="button" variant={systemMode === 'analyze' ? 'default' : 'outline'} className={systemMode === 'analyze' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => handleSystemModeChange('analyze')}>Analyze</Button>
            <Button type="button" variant={systemMode === 'optimize' ? 'default' : 'outline'} className={systemMode === 'optimize' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => handleSystemModeChange('optimize')}>Optimize</Button>
          </div>
        </div>

        <InfraServiceHealthPanel
          apiBase={API}
          getHeaders={getHeaders}
          onUnauthorized={handleAuthExpired}
          statusOverrides={{
            genieacs: serverAvailable ? 'ok' : 'down',
            checkedAt: lastUpdated || null,
          }}
        />

        <div className="space-y-3">
          <div className="bg-[#121212] rounded-xl p-3 border border-white/10 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400 mr-2">Konteks Kerja:</span>
            <Button
              type="button"
              variant={ftthContext === 'live' ? 'default' : 'outline'}
              className={ftthContext === 'live' ? 'btn-primary text-white' : 'btn-secondary'}
              onClick={() => handleFtthContextChange('live')}
            >
              Live
            </Button>
            <Button
              type="button"
              variant={ftthContext === 'planning' ? 'default' : 'outline'}
              className={ftthContext === 'planning' ? 'btn-primary text-white' : 'btn-secondary'}
              onClick={() => handleFtthContextChange('planning')}
            >
              Planning
            </Button>
            <span className="text-xs text-slate-500 ml-auto">
              {ftthContext === 'live'
                ? 'Fokus: validasi aset aktif, pelanggan belum terhubung, dan kualitas jalur live.'
                : 'Fokus: tambah/edit aset draft, pilih titik dari map, lalu validasi rencana.'}
            </span>
          </div>

          <div className="bg-[#121212] rounded-xl p-3 border border-white/10 flex flex-wrap items-center gap-2">
            {ftthContext === 'live' ? (
              <>
                <Button type="button" variant={viewMode === 'validation' ? 'default' : 'outline'} className={viewMode === 'validation' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setViewMode('validation')}>Validasi Area</Button>
                <Button type="button" variant={viewMode === 'explorer' ? 'default' : 'outline'} className={viewMode === 'explorer' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setViewMode('explorer')}>Lihat Asset Existing</Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => navigate('/dadu/pelanggan')}>Pelanggan Belum Terhubung</Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant={planningAction === 'olt' ? 'default' : 'outline'}
                  className={planningAction === 'olt' ? 'btn-primary text-white' : 'btn-secondary'}
                  onClick={() => { setPlanningAction('olt'); setInfraFlowTab('olt'); setSidePanelTab('draft'); }}
                >
                  Tambah OLT
                </Button>
                <Button
                  type="button"
                  variant={planningAction === 'odp' ? 'default' : 'outline'}
                  className={planningAction === 'odp' ? 'btn-primary text-white' : 'btn-secondary'}
                  onClick={() => { setPlanningAction('odp'); setInfraFlowTab('odp'); setSidePanelTab('draft'); }}
                >
                  Tambah ODP
                </Button>
                <Button
                  type="button"
                  variant={planningAction === 'ai-odp' ? 'default' : 'outline'}
                  className={planningAction === 'ai-odp' ? 'btn-primary text-white' : 'btn-secondary'}
                  onClick={() => { setPlanningAction('ai-odp'); setInfraFlowTab('odp'); setSidePanelTab('draft'); }}
                >
                  AI Rekomendasi ODP
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => setViewMode('validation')}>Validasi Draft</Button>
                <Button
                  type="button"
                  variant={showCompareLayer ? 'default' : 'outline'}
                  className={showCompareLayer ? 'btn-primary text-white' : 'btn-secondary'}
                  onClick={() => setShowCompareLayer((prev) => !prev)}
                >
                  {showCompareLayer ? 'Bandingkan Live+Plan: ON' : 'Bandingkan Live+Plan: OFF'}
                </Button>
              </>
            )}
            <p className="text-xs text-slate-500 ml-auto">Klik titik di map utama untuk mengisi koordinat form aktif.</p>
          </div>

          {ftthContext === 'planning' && planningAction === 'ai-odp' && (
            <div className="bg-[#121212] rounded-xl p-4 border border-cyan-500/25 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-cyan-200 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2 py-0.5">AI Rekomendasi ODP</span>
                <span className="text-xs text-slate-400">Mode planning assistant: kandidat harus direview sebelum disimpan.</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                <Select value={aiPlanningUseCase} onValueChange={setAiPlanningUseCase}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                    <SelectValue placeholder="Use case AI" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="next-point" className="text-white">Ekspansi titik berikutnya</SelectItem>
                    <SelectItem value="density" className="text-white">Kepadatan perumahan</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={aiPlanningStrategy} onValueChange={setAiPlanningStrategy}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                    <SelectValue placeholder="Strategi planning" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="cost" className="text-white">Hemat biaya</SelectItem>
                    <SelectItem value="balanced" className="text-white">Seimbang</SelectItem>
                    <SelectItem value="growth" className="text-white">Siap pertumbuhan</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={80}
                  max={600}
                  value={aiPlanningRadius}
                  onChange={(event) => setAiPlanningRadius(Number(event.target.value || 220))}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  placeholder="Radius analisa (m)"
                />
                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A0A] px-3">
                  {ODP_CAPACITY_OPTIONS.map((value) => (
                    <label key={`allow-cap-${value}`} className="inline-flex items-center gap-1 text-xs text-slate-300">
                      <input
                        type="checkbox"
                        checked={Boolean(aiAllowedCapacity[value])}
                        onChange={(event) => setAiAllowedCapacity((prev) => ({ ...prev, [value]: event.target.checked }))}
                      />
                      {value}
                    </label>
                  ))}
                </div>
                <Button type="button" className="btn-primary text-white" onClick={handleRunAiPlanning}>
                  Analisa Area
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="rounded border border-white/10 bg-black/20 px-2 py-1 text-slate-300">
                  Anchor: <span className="text-white">{planningAnchorPoint?.name || '-'}</span>
                </span>
                <span className="rounded border border-white/10 bg-black/20 px-2 py-1 text-slate-300">
                  Rumah belum tercover: <span className="text-white">{uncoveredPlanningCustomers.length}</span>
                </span>
                <span className="rounded border border-white/10 bg-black/20 px-2 py-1 text-slate-300">
                  Kandidat: <span className="text-cyan-300">{aiPlanningCandidates.length}</span>
                </span>
                <span className="rounded border border-white/10 bg-black/20 px-2 py-1 text-slate-300">
                  Run terakhir: <span className="text-white">{aiPlanningLastRunAt ? new Date(aiPlanningLastRunAt).toLocaleString('id-ID') : '-'}</span>
                </span>
              </div>
              {aiPlanningWarnings.length > 0 && (
                <div className="rounded-lg border border-amber-500/35 bg-amber-500/10 p-2 text-xs text-amber-200 space-y-1">
                  {aiPlanningWarnings.map((item, idx) => (
                    <p key={`warn-ai-plan-${idx}`}>• {item}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleAddOlt} className={`bg-[#121212] rounded-xl p-5 border border-white/10 space-y-3 ${(ftthContext !== 'planning' || planningAction !== 'olt' || infraFlowTab !== 'olt') ? 'hidden' : ''}`}>
            <h2 className="text-white font-semibold">{editingOltId ? 'Edit OLT' : 'Tambah OLT'}</h2>
            <p className="text-xs text-gray-500">Urutan input disarankan: Identitas OLT -> NAS -> Lokasi -> Simpan. Lokasi utama sebaiknya dipilih dari map.</p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <button type="button" onClick={() => setOltStep(1)} className={`px-2.5 py-1 rounded-full border ${oltStep === 1 ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200' : 'border-white/10 text-gray-400'}`}>1. Identitas</button>
              <button type="button" onClick={() => setOltStep(2)} className={`px-2.5 py-1 rounded-full border ${oltStep === 2 ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200' : 'border-white/10 text-gray-400'}`}>2. Lokasi</button>
              <button type="button" onClick={() => setOltStep(3)} className={`px-2.5 py-1 rounded-full border ${oltStep === 3 ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200' : 'border-white/10 text-gray-400'}`}>3. Konfirmasi</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={oltForm.name} onChange={(e) => setOltForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nama OLT" className="bg-[#0A0A0A] border-white/10 text-white" required />
              <Input value={oltForm.code} onChange={(e) => setOltForm((p) => ({ ...p, code: e.target.value }))} placeholder="Kode OLT" className="bg-[#0A0A0A] border-white/10 text-white" />
              <div className="col-span-2">
              <SearchableComboBox
                value={oltForm.router_id || ''}
                onChange={(value) => setOltForm((p) => ({ ...p, router_id: value || '' }))}
                options={nasComboOptions}
                placeholder="Pilih NAS / Router"
                searchPlaceholder="Cari NAS..."
                emptyLabel="NAS tidak ditemukan"
                includeEmpty
                emptyValueLabel="Tanpa NAS"
                onSearch={searchNasLookup}
                minSearchChars={2}
                searchContextKey="ftth-form-olt-nas"
              />
            </div>
              <Select value={oltForm.kriteria || 'live'} onValueChange={(v) => setOltForm((p) => ({ ...p, kriteria: normalizeInfraKriteria(v) }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white col-span-2">
                  <SelectValue placeholder="Kriteria OLT" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {FTTH_CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={oltForm.location} onChange={(e) => setOltForm((p) => ({ ...p, location: e.target.value }))} placeholder="Lokasi" className="bg-[#0A0A0A] border-white/10 text-white col-span-2" />
              <div className="col-span-2 flex justify-end">
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => handleGetCurrentLocation('olt')}>
                  <LocateFixed className="w-4 h-4 mr-2" />Ambil Lokasi Terkini
                </Button>
              </div>
              <Input type="number" step="any" value={oltForm.latitude} onChange={(e) => setOltForm((p) => ({ ...p, latitude: e.target.value }))} placeholder="Latitude" className="bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="number" step="any" value={oltForm.longitude} onChange={(e) => setOltForm((p) => ({ ...p, longitude: e.target.value }))} placeholder="Longitude" className="bg-[#0A0A0A] border-white/10 text-white" />
              <details className="col-span-2 rounded-lg border border-white/10 bg-black/20 p-2">
                <summary className="cursor-pointer text-xs text-slate-300">Butuh picker presisi? Buka mini map OLT</summary>
                <div className="pt-2">
                  <LeafletPinPicker
                    title="Lokasi OLT (klik peta atau geser pin)"
                    latitude={oltForm.latitude}
                    longitude={oltForm.longitude}
                    fallbackCenter={MATAKALI_FORM_CENTER}
                    onChange={(latitude, longitude) => handleMapLocationChange('olt', latitude, longitude)}
                  />
                </div>
              </details>
              <Input type="number" value={oltForm.x} onChange={(e) => setOltForm((p) => ({ ...p, x: e.target.value }))} placeholder="Posisi X" className="bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="number" value={oltForm.y} onChange={(e) => setOltForm((p) => ({ ...p, y: e.target.value }))} placeholder="Posisi Y" className="bg-[#0A0A0A] border-white/10 text-white" />
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[11px] text-gray-300">
                NAS: <span className="ml-1 text-white">{nasByRouterId[String(oltForm.router_id)]?.name || '-'}</span>
              </span>
              <span className="inline-flex items-center rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[11px] text-gray-300">
                Kriteria: <span className="ml-1 text-white">{getInfraCategoryLabel(oltForm.kriteria)}</span>
              </span>
            </div>
            <div className="mt-3 border-t border-white/10 pt-3 flex gap-2">
              {oltStep > 1 && (
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleOltPrevStep}>Kembali</Button>
              )}
              {oltStep < 3 ? (
                <Button type="button" className="btn-primary text-white" onClick={handleOltNextStep}>Lanjut</Button>
              ) : (
                <Button type="submit" className="btn-primary text-white">{editingOltId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}{editingOltId ? 'Simpan Perubahan' : 'Tambah OLT'}</Button>
              )}
              {editingOltId && (
                <Button type="button" variant="outline" className="btn-secondary" onClick={resetOltForm}>
                  <X className="w-4 h-4 mr-2" />Batal
                </Button>
              )}
            </div>
          </form>

          <form onSubmit={handleAddOdp} className={`bg-[#121212] rounded-xl p-5 border border-white/10 space-y-3 ${(ftthContext !== 'planning' || planningAction !== 'odp' || infraFlowTab !== 'odp') ? 'hidden' : ''}`}>
            <h2 className="text-white font-semibold">{editingOdpId ? 'Edit ODP' : 'Tambah ODP'}</h2>
            <p className="text-xs text-gray-500">Urutan input disarankan: Identitas ODP → NAS → OLT induk → Lokasi → Simpan.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={odpForm.name} onChange={(e) => setOdpForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nama ODP" className="bg-[#0A0A0A] border-white/10 text-white" required />
              <Input value={odpForm.code} onChange={(e) => setOdpForm((p) => ({ ...p, code: e.target.value }))} placeholder="Kode ODP" className="bg-[#0A0A0A] border-white/10 text-white" />
              <div className="col-span-2">
                <SearchableComboBox
                  value={odpForm.router_id || ''}
                  onChange={(value) => {
                    const nextRouter = value || '';
                    setOdpForm((p) => ({ ...p, router_id: nextRouter, olt_id: '' }));
                  }}
                  options={nasComboOptions}
                  placeholder="Pilih NAS / Router"
                  searchPlaceholder="Cari NAS..."
                  emptyLabel="NAS tidak ditemukan"
                  includeEmpty
                  emptyValueLabel="Tanpa NAS"
                  onSearch={searchNasLookup}
                  minSearchChars={2}
                  searchContextKey="ftth-form-odp-nas"
                />
              </div>
              <Select value={odpForm.kriteria || 'live'} onValueChange={(v) => setOdpForm((p) => ({ ...p, kriteria: normalizeInfraKriteria(v) }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white col-span-2">
                  <SelectValue placeholder="Kriteria ODP" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {FTTH_CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(odpForm.suggested_capacity || DEFAULT_ODP_CAPACITY)} onValueChange={(v) => setOdpForm((p) => ({ ...p, suggested_capacity: String(v || DEFAULT_ODP_CAPACITY) }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white col-span-2">
                  <SelectValue placeholder="Kapasitas ODP (8/16/24)" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {ODP_CAPACITY_OPTIONS.map((cap) => (
                    <SelectItem key={`odp-capacity-${cap}`} value={String(cap)} className="text-white">{`ODP ${cap}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="col-span-2">
                <SearchableComboBox
                  value={odpForm.olt_id || ''}
                  onChange={(value) => {
                    const nextOltId = value || '';
                    const selectedOlt = [...olts, ...oltLookupItems].find((item) => String(item.id) === String(nextOltId));
                    setOdpForm((p) => ({ ...p, olt_id: nextOltId, router_id: selectedOlt?.router_id || p.router_id || '' }));
                  }}
                  options={oltOptionsForOdpFormCombo}
                  placeholder="Pilih OLT"
                  searchPlaceholder="Cari OLT..."
                  emptyLabel="OLT tidak ditemukan"
                  includeEmpty
                  emptyValueLabel="Tanpa OLT"
                  onSearch={(query) => searchOltLookup(query, odpForm.router_id)}
                  minSearchChars={2}
                  searchContextKey={`ftth-form-odp-olt:${odpForm.router_id || 'all'}`}
                />
              </div>
              <Input value={odpForm.address} onChange={(e) => setOdpForm((p) => ({ ...p, address: e.target.value }))} placeholder="Alamat ODP" className="bg-[#0A0A0A] border-white/10 text-white col-span-2" />
              <div className="col-span-2 flex justify-end">
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => handleGetCurrentLocation('odp')}>
                  <LocateFixed className="w-4 h-4 mr-2" />Ambil Lokasi Terkini
                </Button>
              </div>
              <Input type="number" step="any" value={odpForm.latitude} onChange={(e) => setOdpForm((p) => ({ ...p, latitude: e.target.value }))} placeholder="Latitude" className="bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="number" step="any" value={odpForm.longitude} onChange={(e) => setOdpForm((p) => ({ ...p, longitude: e.target.value }))} placeholder="Longitude" className="bg-[#0A0A0A] border-white/10 text-white" />
              <details className="col-span-2 rounded-lg border border-white/10 bg-black/20 p-2">
                <summary className="cursor-pointer text-xs text-slate-300">Butuh picker presisi? Buka mini map ODP</summary>
                <div className="pt-2">
                  <LeafletPinPicker
                    title="Lokasi ODP (klik peta atau geser pin)"
                    latitude={odpForm.latitude}
                    longitude={odpForm.longitude}
                    fallbackCenter={MATAKALI_FORM_CENTER}
                    onChange={(latitude, longitude) => handleMapLocationChange('odp', latitude, longitude)}
                  />
                </div>
              </details>
              <Input type="number" value={odpForm.x} onChange={(e) => setOdpForm((p) => ({ ...p, x: e.target.value }))} placeholder="Posisi X" className="bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="number" value={odpForm.y} onChange={(e) => setOdpForm((p) => ({ ...p, y: e.target.value }))} placeholder="Posisi Y" className="bg-[#0A0A0A] border-white/10 text-white" />
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[11px] text-gray-300">
                NAS: <span className="ml-1 text-white">{nasByRouterId[String(odpForm.router_id)]?.name || '-'}</span>
              </span>
              <span className="inline-flex items-center rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[11px] text-gray-300">
                OLT: <span className="ml-1 text-white">{allOltsById[String(odpForm.olt_id)]?.name || '-'}</span>
              </span>
              <span className="inline-flex items-center rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[11px] text-gray-300">
                Kriteria: <span className="ml-1 text-white">{getInfraCategoryLabel(odpForm.kriteria)}</span>
              </span>
              <span className="inline-flex items-center rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-200">
                Kapasitas ODP: <span className="ml-1 text-white">{Number(odpForm.suggested_capacity || DEFAULT_ODP_CAPACITY)}</span>
              </span>
            </div>
            {Number(odpForm.suggested_capacity || DEFAULT_ODP_CAPACITY) === 8 && aiPlanningSelectedCandidate && aiPlanningSelectedCandidate.housesCovered >= 14 && (
              <div className="rounded border border-amber-500/35 bg-amber-500/10 p-2 text-xs text-amber-200">
                Warning: klaster kandidat {aiPlanningSelectedCandidate.rank} estimasi {aiPlanningSelectedCandidate.housesCovered} rumah, ODP 8 berisiko cepat penuh.
              </div>
            )}
            {Number(odpForm.suggested_capacity || DEFAULT_ODP_CAPACITY) === 24 && aiPlanningSelectedCandidate && aiPlanningSelectedCandidate.housesCovered <= 7 && (
              <div className="rounded border border-amber-500/35 bg-amber-500/10 p-2 text-xs text-amber-200">
                Warning: klaster kandidat {aiPlanningSelectedCandidate.rank} kecil, ODP 24 berpotensi underutilized.
              </div>
            )}
            <div className="mt-3 border-t border-white/10 pt-3 flex gap-2">
              {odpStep > 1 && (
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleOdpPrevStep}>Kembali</Button>
              )}
              {odpStep < 3 ? (
                <Button type="button" className="btn-primary text-white" onClick={handleOdpNextStep}>Lanjut</Button>
              ) : (
                <Button type="submit" className="btn-primary text-white">{editingOdpId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}{editingOdpId ? 'Simpan Perubahan' : 'Tambah ODP'}</Button>
              )}
              {editingOdpId && (
                <Button type="button" variant="outline" className="btn-secondary" onClick={resetOdpForm}>
                  <X className="w-4 h-4 mr-2" />Batal
                </Button>
              )}
            </div>
          </form>
        </div>

        <div
          ref={mapFullscreenRef}
          className={isMapFullscreen ? 'relative h-screen w-screen bg-[#0b1220] p-3 md:p-4' : ''}
        >
          <div className={isMapFullscreen ? 'relative h-full' : 'grid xl:grid-cols-[minmax(0,1fr)_300px] gap-4'}>
            <div className={isMapFullscreen ? 'relative h-full rounded-xl border border-white/15 overflow-hidden bg-[#121212]' : 'bg-[#121212] rounded-xl border border-white/10 overflow-hidden'}>
            {!hasTopologyNodes ? (
              <div className="h-[640px] rounded-lg border border-white/10 bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center">
                <p className="text-2xl font-semibold text-white">Topologi Belum Tersedia</p>
                <p className="text-gray-400 mt-2">Lengkapi koordinat OLT, ODP, dan pelanggan agar jalur FTTH tampil di peta.</p>
              </div>
            ) : (
              <div className={isMapFullscreen ? 'relative h-full' : 'relative h-[80vh] min-h-[700px]'}>
                <MapContainer
                  center={[DEFAULT_MAP_CENTER.latitude, DEFAULT_MAP_CENTER.longitude]}
                  zoom={13}
                  className="h-full w-full staff-monitoring-map"
                  scrollWheelZoom
                >
                  <LeafletTileLayer />

                  <FitBoundsController points={mapWorkspacePointsWithAi} />
                  <MapClickPicker
                    enabled={ftthContext === 'planning' && (planningAction === 'olt' || planningAction === 'odp')}
                    onPick={handleMainMapPointPick}
                  />

                  {ftthContext === 'planning' && planningAction === 'ai-odp' && aiPlanningCandidates.map((candidate) => {
                    const selected = aiPlanningSelectedCandidateId === candidate.id;
                    const candidatePoint = [Number(candidate.latitude), Number(candidate.longitude)];
                    const anchorPoint = planningAnchorPoint?.point;
                    const pathPositions = Array.isArray(anchorPoint)
                      ? buildDistributionPath(anchorPoint, candidatePoint, `ai-plan-${candidate.id}`)
                      : [candidatePoint];
                    return (
                      <React.Fragment key={`ai-candidate-${candidate.id}`}>
                        {Array.isArray(anchorPoint) && (
                          <>
                            <Polyline
                              positions={pathPositions}
                              pathOptions={{
                                color: selected ? '#22d3ee' : '#f59e0b',
                                weight: selected ? 3.6 : 2.4,
                                opacity: selected ? 0.95 : 0.65,
                                dashArray: selected ? '10 5' : '6 6',
                                className: selected ? 'planning-ai-path-selected' : 'planning-ai-path',
                              }}
                            />
                            <LeafletFlowDirection
                              positions={pathPositions}
                              color={selected ? '#22d3ee' : '#f59e0b'}
                              offset="15%"
                              repeat="58px"
                              pixelSize={8}
                              className={selected ? 'planning-ai-path-selected' : 'planning-ai-path'}
                            />
                          </>
                        )}
                        <Circle
                          center={candidatePoint}
                          radius={Math.max(90, Math.min(300, Number(aiPlanningRadius || 220)))}
                          pathOptions={{
                            color: selected ? '#22d3ee' : '#f59e0b',
                            fillColor: selected ? '#22d3ee' : '#f59e0b',
                            fillOpacity: selected ? 0.12 : 0.07,
                            weight: selected ? 1.8 : 1.2,
                            className: selected ? 'planning-candidate-coverage is-selected' : 'planning-candidate-coverage',
                          }}
                        />
                        <Marker
                          position={candidatePoint}
                          icon={createPlanningCandidateIcon(candidate, selected)}
                          eventHandlers={{ click: () => setAiPlanningSelectedCandidateId(candidate.id) }}
                        >
                          <Popup className="ktc-map-popup">
                            <div className="text-sm min-w-[220px] space-y-1">
                              <p className="font-semibold">{candidate.label}</p>
                              <p>Kapasitas rekomendasi: <span className="font-semibold text-cyan-300">{candidate.suggestedCapacity}</span></p>
                              <p>Estimasi rumah tercover: <span className="font-semibold text-white">{candidate.housesCovered}</span></p>
                              <p>Jarak ke anchor: {candidate.distanceFromAnchor} m</p>
                              <p>Total skor: <span className="font-semibold text-amber-300">{candidate.totalScore}</span></p>
                              {candidate.warnings?.map((warning, idx) => (
                                <p key={`${candidate.id}-warn-${idx}`} className="text-[11px] text-amber-300">• {warning}</p>
                              ))}
                              <div className="grid grid-cols-2 gap-2 pt-2">
                                <Button type="button" size="sm" className="btn-primary text-white" onClick={() => handleApplyAiCandidate(candidate)}>Gunakan Titik Ini</Button>
                                <Button type="button" size="sm" variant="outline" className="btn-secondary" onClick={() => handleSimulateAiCandidate(candidate)}>Simulasikan</Button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      </React.Fragment>
                    );
                  })}

                  {contextOdps.map((odp) => {
                    if (!hasValidCoordinates(odp.latitude, odp.longitude)) return null;
                    const odpId = String(odp.id || '').trim();
                    const odpStatus = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
                    const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                    const hasAiAnomaly = aiModeEnabled && !!anomalyByOdpId[odpId];
                    const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                    return (
                      <React.Fragment key={"odp-" + odp.id}>
                      <Marker
                        position={odpPosition}
                        icon={createODPMarker(odpStatus)}
                        eventHandlers={{ click: () => setFocusedNode({ type: 'odp', id: String(odp.id || '') }) }}
                      >
                        <Popup className="ktc-map-popup">
                          <div className="text-sm min-w-[190px]">
                            <p className="font-semibold">{odp.name}</p>
                            <p>Status: <span className={odpStatus === 'online' ? 'ktc-status-online-text' : 'ktc-status-offline-text'}>{odpStatus === 'online' ? 'Online' : odpStatus === 'offline' ? 'Offline' : 'Unknown'}</span></p>
                            <p>Jumlah pelanggan: {pelangganCount}</p>
                            <p>Kode: {odp.code || "-"}</p>
                            <p>Kriteria: {getInfraCategoryLabel(odp.kriteria)}</p>
                            <p>OLT: {allOltsById[odp.olt_id]?.name || "-"}</p>
                            <p>Koordinat: {formatCoordinate(odp.latitude)}, {formatCoordinate(odp.longitude)}</p>
                            <button
                              type="button"
                              onClick={() => handleEditOdp(odp)}
                              className="mt-3 w-full rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 px-3 py-1.5 text-xs font-semibold hover:bg-blue-500/30"
                            >
                              Edit ODP
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                      {hasAiAnomaly && (
                        <Circle
                          center={odpPosition}
                          radius={38}
                          pathOptions={{
                            className: 'ai-node-ring',
                            color: '#ef4444',
                            fillColor: '#ef4444',
                            fillOpacity: 0.08,
                            weight: 1.8,
                            opacity: 0.9,
                          }}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}

                  {contextOlts.map((olt) => {
                    if (!hasValidCoordinates(olt.latitude, olt.longitude)) return null;
                    const oltId = String(olt.id || '').trim();
                    const oltStatus = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
                    const pelangganCount = Number(oltCustomerCountById[oltId] || 0);
                    const hasAiAnomaly = aiModeEnabled && !!anomalyByOltId[oltId];
                    const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
                    return (
                      <React.Fragment key={"olt-" + olt.id}>
                      <Marker
                        position={oltPosition}
                        icon={createOLTMarker(oltStatus)}
                        eventHandlers={{ click: () => setFocusedNode({ type: 'olt', id: String(olt.id || '') }) }}
                      >
                        <Popup className="ktc-map-popup">
                          <div className="text-sm min-w-[190px]">
                            <p className="font-semibold">{olt.name}</p>
                            <p>Status: <span className={oltStatus === 'online' ? 'ktc-status-online-text' : 'ktc-status-offline-text'}>{oltStatus === 'online' ? 'Online' : oltStatus === 'offline' ? 'Offline' : 'Unknown'}</span></p>
                            <p>Jumlah pelanggan: {pelangganCount}</p>
                            <p>Kode: {olt.code || "-"}</p>
                            <p>Kriteria: {getInfraCategoryLabel(olt.kriteria)}</p>
                            <p>Lokasi: {olt.location || "-"}</p>
                            <p>Koordinat: {formatCoordinate(olt.latitude)}, {formatCoordinate(olt.longitude)}</p>
                            <button
                              type="button"
                              onClick={() => handleEditOlt(olt)}
                              className="mt-3 w-full rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 px-3 py-1.5 text-xs font-semibold hover:bg-blue-500/30"
                            >
                              Edit OLT
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                      {hasAiAnomaly && (
                        <Circle
                          center={oltPosition}
                          radius={40}
                          pathOptions={{
                            className: 'ai-node-ring',
                            color: '#ef4444',
                            fillColor: '#ef4444',
                            fillOpacity: 0.08,
                            weight: 1.8,
                            opacity: 0.9,
                          }}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}

                  {contextOdps.map((odp) => {
                    const olt = odp?.olt_id ? allOltsById[odp.olt_id] : null;
                    if (!olt) return null;
                    if (!hasValidCoordinates(odp.latitude, odp.longitude) || !hasValidCoordinates(olt.latitude, olt.longitude)) return null;

                    const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
                    const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                    const oltId = String(olt.id || '').trim();
                    const odpId = String(odp.id || '').trim();
                    const oltStatus = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
                    const odpStatus = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
                    const hasAiAnomaly = aiModeEnabled && (!!anomalyByOdpId[odpId] || !!anomalyByOltId[oltId]);
                    const connection = drawConnection(
                      oltPosition,
                      odpPosition,
                      { error: hasAiAnomaly || oltStatus !== 'online' || odpStatus !== 'online', weight: 2.8 },
                    );
                    const connectionClassName = `${connection.className}${hasAiAnomaly ? ' ai-link-alert' : ''}`;

                    const corePositions = buildDistributionPath(oltPosition, odpPosition, `core-${olt.id}-${odp.id}`);
                    const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                    const inferredCoreLoad = Math.min(100, pelangganCount * 12 + (hasAiAnomaly ? 12 : 0));
                    const coreBandwidth = getBandwidthStyle(
                      resolveLinkLoad(odp) || inferredCoreLoad,
                      {
                        status: hasAiAnomaly ? 'offline' : (oltStatus === 'online' && odpStatus === 'online' ? 'online' : 'offline'),
                        useGradient: true,
                        minWeight: 2.4,
                        maxWeight: 7.2,
                      },
                    );
                    const coreColor = coreBandwidth.color || connection.pathOptions?.color || '#06b6d4';
                    const focused = isPathFocused(oltId, odpId, null);
                    return (
                      <React.Fragment key={`line-olt-odp-${odp.id}`}>
                        <Polyline
                          positions={corePositions}
                          className={`${connectionClassName} ${coreBandwidth.glowClass}`.trim()}
                          pathOptions={{
                            ...connection.pathOptions,
                            color: coreColor,
                            weight: coreBandwidth.weight,
                            opacity: focused ? 0.96 : 0.68,
                            className: connectionClassName,
                          }}
                        />
                        <LeafletFlowDirection
                          positions={corePositions}
                          color={coreColor}
                          offset="9%"
                          repeat="72px"
                          pixelSize={8}
                          className={`${connectionClassName} ${coreBandwidth.glowClass}`.trim()}
                        />
                      </React.Fragment>
                    );
                  })}

                                    {odpCoverageZones.map((zone) => (
                    <Circle
                      key={`odp-coverage-${zone.id}`}
                      center={[zone.latitude, zone.longitude]}
                      radius={zone.radius}
                      pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.2, weight: 1.2, opacity: 0.85, className: 'customer-presence' }}
                    />
                  ))}
                  {contextCustomersForMap.map((customer) => {
                    const odpId = customer.linked_odp_id || customer.odp_id;
                    const odp = odpId ? odpsById[odpId] : null;
                    if (!odp) return null;
                    if (!hasValidCoordinates(odp.latitude, odp.longitude)) return null;

                    const insight = aiNodeInsights.byId[String(customer.id || '')];
                    const hasAiAnomaly = aiModeEnabled && !!insight?.anomaly?.hasAnomaly;
                    const baseLineClass = customer.status === "online"
                      ? "customer-link customer-link-online"
                      : customer.status === "offline"
                        ? "customer-link customer-link-offline"
                        : "customer-link customer-link-unknown";
                    const lineClass = `${baseLineClass}${hasAiAnomaly ? ' ai-link-alert' : ''}`;

                    const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];

                    const customerPositions = [
                      odpPosition,
                      [Number(customer.latitude), Number(customer.longitude)],
                    ];
                    const customerBandwidth = getBandwidthStyle(
                      estimateCustomerLoad(customer),
                      {
                        status: hasAiAnomaly ? 'offline' : customer.status,
                        useGradient: true,
                        minWeight: 2.2,
                        maxWeight: 6.6,
                      },
                    );
                    const customerColor = hasAiAnomaly ? '#ef4444' : customerBandwidth.color;
                    const focused = isPathFocused(null, odpId, customer.id);
                    return (
                      <React.Fragment key={`line-odp-customer-${customer.id}`}>
                        <Polyline
                          positions={customerPositions}
                          pathOptions={{
                            color: customerColor,
                            weight: customerBandwidth.weight,
                            opacity: focused ? 0.96 : 0.62,
                            dashArray: '10 7',
                            className: lineClass,
                          }}
                          className={`${lineClass} ${customerBandwidth.glowClass}`.trim()}
                        />
                        <LeafletFlowDirection
                          positions={customerPositions}
                          color={customerColor}
                          offset="12%"
                          repeat="52px"
                          pixelSize={7}
                          className={`${lineClass} ${customerBandwidth.glowClass}`.trim()}
                        />
                      </React.Fragment>
                    );
                  })}

                  
                   {contextCustomersForMap.map((customer) => {
                    const color = getStatusColor(customer.status);
                    const insight = aiNodeInsights.byId[String(customer.id || '')];
                    return (
                      <Marker
                        key={"customer-" + customer.id}
                        position={[Number(customer.latitude), Number(customer.longitude)]}
                        icon={createWifiIcon(customer.status, {
                          anomaly: aiModeEnabled && !!insight?.anomaly?.hasAnomaly,
                          predictive: aiModeEnabled && (insight?.predictions?.length || 0) > 0,
                        })}
                        eventHandlers={{ click: () => setFocusedNode({ type: 'customer', id: String(customer.id || '') }) }}
                      >
                        <Popup>
                          <div className="text-sm min-w-[220px]">
                            <p className="font-semibold text-base mb-1">{customer.name}</p>
                            <p>ID: {customer.customer_id}</p>
                            <p>ODP: {customer.odp || "-"}</p>
                            <p>Status ONU: <span style={{ color, fontWeight: 700 }}>{getConnectionLabel(customer.status)}</span></p>
                            <p>RX Power/Redaman: {formatSignal(customer)}</p>
                            {aiModeEnabled && insight?.anomaly?.hasAnomaly && (
                              <div className="rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                                {insight.anomaly.issues.map((issue) => <p key={`${customer.id}-${issue.code}`}>• {issue.label}</p>)}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => handleEditCustomer(customer)}
                              className="mt-3 w-full rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 px-3 py-1.5 text-xs font-semibold hover:bg-blue-500/30"
                            >
                              Edit Pelanggan
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
                <div className="absolute right-16 top-3 z-[620]">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="btn-secondary bg-white/95 text-slate-800 hover:bg-white"
                    onClick={handleToggleMapFullscreen}
                  >
                    {isMapFullscreen ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />}
                    {isMapFullscreen ? 'Keluar Fullscreen' : 'Fullscreen'}
                  </Button>
                </div>
              </div>
            )}
          </div>

            <div className={isMapFullscreen ? 'absolute right-3 top-14 z-[650] w-[min(92vw,300px)] max-h-[calc(100vh-4.5rem)] overflow-auto rounded-xl bg-[#0f172a]/95 p-2 backdrop-blur-sm space-y-3' : 'space-y-4'}>
            <div className="bg-[#121212] rounded-xl p-3 border border-white/10">
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant={sidePanelTab === 'insight' ? 'default' : 'outline'} className={sidePanelTab === 'insight' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('insight')}>Insight</Button>
                <Button type="button" size="sm" variant={sidePanelTab === 'capacity' ? 'default' : 'outline'} className={sidePanelTab === 'capacity' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('capacity')}>Capacity</Button>
                <Button type="button" size="sm" variant={sidePanelTab === 'asset' ? 'default' : 'outline'} className={sidePanelTab === 'asset' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('asset')}>Asset</Button>
                <Button type="button" size="sm" variant={sidePanelTab === 'legend' ? 'default' : 'outline'} className={sidePanelTab === 'legend' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('legend')}>Legenda</Button>
                <Button type="button" size="sm" variant={sidePanelTab === 'validation' ? 'default' : 'outline'} className={sidePanelTab === 'validation' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('validation')}>Validation</Button>
                <Button type="button" size="sm" variant={sidePanelTab === 'draft' ? 'default' : 'outline'} className={sidePanelTab === 'draft' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setSidePanelTab('draft')}>Draft</Button>
              </div>
            </div>

            {sidePanelTab === 'capacity' && (
              <div className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-white font-semibold">Capacity Insight</h3>
                  <span className="text-[10px] text-slate-400">ODP + NAS</span>
                </div>
                <div className="space-y-2">
                  {capacityPrediction.insights.length === 0 ? (
                    <p className="text-xs text-slate-500">Belum ada insight kapasitas.</p>
                  ) : capacityPrediction.insights.map((insight) => (
                    <p key={`ftth-capacity-insight-${insight}`} className="text-xs text-slate-200">• {insight}</p>
                  ))}
                </div>
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">ODP Berisiko</p>
                  <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
                    {capacityPrediction.odps.slice(0, 8).map((odp) => (
                      <div key={`ftth-cap-odp-${odp.id}`} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-200 truncate">{odp.name}</span>
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${getRiskBadgeClass(odp.risk.tone)}`}>{odp.risk.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">NAS Growth</p>
                  <div className="space-y-1.5 max-h-32 overflow-auto pr-1">
                    {capacityPrediction.nas.slice(0, 6).map((nas) => (
                      <div key={`ftth-cap-nas-${nas.id}`} className="text-xs text-slate-200">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{nas.name}</span>
                          <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${getRiskBadgeClass(nas.risk.tone)}`}>{nas.risk.label}</span>
                        </div>
                        <p className="text-[10px] text-slate-400">Aktif {nas.activeCount} • +{nas.growth30d} /30d</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Capacity Recommendation</p>
                    <span className="text-[10px] text-slate-500">Strategi: {aiPlanningStrategy === 'cost' ? 'Hemat biaya' : aiPlanningStrategy === 'growth' ? 'Siap pertumbuhan' : 'Seimbang'}</span>
                  </div>
                  {capacityRecommendations.recommendations.length === 0 ? (
                    <p className="text-xs text-slate-500">Belum ada rekomendasi kapasitas pada scope ini.</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-auto pr-1">
                      {capacityRecommendations.recommendations.slice(0, 5).map((item) => (
                        <div key={`ftth-capacity-rec-${item.id}`} className="rounded-md border border-white/10 bg-[#06080d] p-2.5">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getUrgencyBadgeClass(item?.urgency?.tone)}`}>{item?.urgency?.label || 'Rendah'}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getRecommendationTypeBadgeClass(item?.actionType)}`}>{getRecommendationTypeLabel(item?.actionType)}</span>
                            <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-slate-500/40 bg-slate-600/10 text-slate-200">{String(item?.sourceType || '-').toUpperCase()}</span>
                            {Number.isFinite(Number(item?.suggestedOdpCapacity)) ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-cyan-500/45 bg-cyan-500/10 text-cyan-200">ODP {Number(item.suggestedOdpCapacity)}</span>
                            ) : null}
                          </div>
                          <p className="text-xs text-white font-medium">{item.title}</p>
                          <p className="text-[11px] text-cyan-200 mt-0.5">Aksi disarankan: <span className="text-slate-200">{item.suggestedAction || item.summary}</span></p>
                          <p className="text-[11px] text-slate-400 mt-1">Alasan utama: {item.reasonLines?.[0] || '-'}</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[10px] btn-secondary"
                              onClick={() => navigate(item?.links?.monitoring || '/dadu/monitoring')}
                            >
                              Lihat Monitoring
                            </Button>
                            {item.sourceType === 'odp' ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-[10px] btn-secondary"
                                onClick={() => {
                                  setFtthContext('planning');
                                  setPlanningAction('ai-odp');
                                  setSidePanelTab('draft');
                                  const linked = capacityPrediction.byOdpId[String(item.sourceId || '')];
                                  if (linked?.oltId) setSelectedOltId(String(linked.oltId));
                                  toast.success(`Planning difokuskan untuk ${item.sourceLabel}.`);
                                }}
                              >
                                Analisis Area
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-[10px] btn-secondary"
                                onClick={() => navigate(item?.links?.nas || '/dadu/voucher?tab=nas')}
                              >
                                Lihat NAS
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {sidePanelTab === 'legend' && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-semibold mb-3">Legenda Infrastruktur & Garis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-300"><span className={getInfraNodePreviewClassName('olt', 'live')} /> OLT Live (terpasang)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className={getInfraNodePreviewClassName('olt', 'plan')} /> OLT Plan (perencanaan)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className={getInfraNodePreviewClassName('odp', 'live')} /> ODP Live (terpasang)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className={getInfraNodePreviewClassName('odp', 'plan')} /> ODP Plan (perencanaan)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-blue-500 inline-block" /> OLT ke ODP (keduanya Live)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-amber-500 inline-block" /> OLT ke ODP (ada Plan)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-4 h-4 rounded-full border border-cyan-300 bg-cyan-500/20 inline-block" /> AI Candidate ODP (Planning)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-cyan-400 inline-block" /> Jalur rekomendasi AI (preview)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-blue-500 inline-block" /> ODP ke Rumah (ONU online)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-red-500 inline-block" /> ODP ke Pelanggan (loss/offline)</div>
                <div className="flex items-center gap-2 text-gray-300"><span className="w-8 h-1 bg-gray-500 inline-block" /> Jalur belum lengkap (OLT/ODP belum dipilih)</div>
              </div>
            </div>
            )}

            {sidePanelTab === 'insight' && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10 noc-glass-panel noc-ai-panel">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="text-white font-semibold">AI Insight</h3>
                <span className={`noc-ai-state ${aiModeEnabled ? 'is-on' : 'is-off'}`}>{aiModeEnabled ? 'ON' : 'OFF'}</span>
              </div>
              {!aiModeEnabled ? (
                <p className="text-xs text-slate-400">Aktifkan AI MODE untuk mendeteksi anomaly node dan prediksi potensi gangguan.</p>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.totalAnomaly} node anomaly</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> {aiNodeInsights.summary.latencyHigh} latency tinggi</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.signalWeak} sinyal lemah</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.offline} offline</div>
                  <div className="noc-ai-item"><span className="dot is-info" /> {aiNodeInsights.summary.predictiveCount} predictive alert</div>
                  </div>
                )}
              </div>
            )}

            {sidePanelTab === 'insight' && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold">Explorer Sidebar</h3>
                {focusedNode?.id ? (
                  <Button type="button" variant="outline" className="btn-secondary h-8" onClick={() => setFocusedNode(null)}>
                    Hapus Fokus Path
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant={viewMode === 'explorer' ? 'default' : 'outline'} className={viewMode === 'explorer' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setViewMode('explorer')}>Explorer</Button>
                <Button type="button" variant={viewMode === 'validation' ? 'default' : 'outline'} className={viewMode === 'validation' ? 'btn-primary text-white' : 'btn-secondary'} onClick={() => setViewMode('validation')}>Validasi</Button>
              </div>
              {viewMode === 'explorer' ? (
                <div className="space-y-2 text-xs">
                  <Input value={explorerQuery} onChange={(event) => setExplorerQuery(event.target.value)} placeholder="Cari OLT/ODP/Pelanggan..." className="bg-[#050505] border-white/10 text-white h-9" />
                  <p className="text-slate-400">OLT: <span className="text-white">{explorerOlts.length}</span> • ODP: <span className="text-white">{explorerOdps.length}</span> • Pelanggan: <span className="text-white">{explorerCustomers.length}</span></p>
                  <p className="text-slate-400">Graph: <span className="text-white">{Number(topologyGraphMeta?.summary?.nodes || 0)} node</span> • <span className="text-white">{Number(topologyGraphMeta?.summary?.edges || 0)} edge</span></p>
                </div>
              ) : (
                <div className="space-y-2 text-xs">
                  <p className="text-slate-300">Issue High: <span className="text-red-300 font-semibold">{validationSummary.high}</span></p>
                  <p className="text-slate-300">Issue Medium: <span className="text-amber-300 font-semibold">{validationSummary.medium}</span></p>
                  <p className="text-slate-300">Issue Low: <span className="text-cyan-300 font-semibold">{validationSummary.low}</span></p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button type="button" variant="outline" className="btn-secondary h-8" onClick={() => navigate('/dadu/pelanggan')}>Pindahkan Pelanggan</Button>
                    <Button type="button" variant="outline" className="btn-secondary h-8" onClick={() => { setInfraFlowTab('odp'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Tambah ODP</Button>
                  </div>
                  </div>
                )}
              </div>
            )}

            {sidePanelTab === 'insight' && (
            <div className="bg-[#eaf2ff] rounded-xl p-4 border border-[#93c5fd] text-sm text-slate-700 space-y-2">
              <p>Filter: <span className="text-slate-900 font-medium">{infraFilter === 'all' ? 'Semua' : infraFilter === 'live' ? 'Live' : 'Plan'}</span></p>
              <p>Filter OLT: <span className="text-slate-900 font-medium">{selectedOltId === 'all' ? 'Semua OLT' : (olts.find((olt) => String(olt.id) === String(selectedOltId))?.name || selectedOltId)}</span></p>
              <p>Total OLT tampil: <span className="text-slate-900 font-medium">{contextOlts.length}</span> <span className="text-slate-500">/ {olts.length}</span></p>
              <p>Total ODP tampil: <span className="text-slate-900 font-medium">{contextOdps.length}</span> <span className="text-slate-500">/ {odps.length}</span></p>
              <div className="flex flex-wrap gap-2">
                <MapStatusPill
                  label="Status GenieACS"
                  value={serverAvailable ? 'Terhubung' : 'Tidak terhubung'}
                  variant={serverAvailable ? 'success' : 'danger'}
                  tone="light"
                />
                <MapStatusPill
                  label="Status Realtime"
                  value="Aktif (polling status)"
                  variant="info"
                  tone="light"
                />
              </div>
              <p className="text-slate-600">Pembaruan Terakhir: <span className="text-slate-900 font-medium">{lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</span></p>
              <div className="flex flex-wrap gap-2">
                <MapStatusPill label="Pelanggan Terhubung" value={linkedCustomersForView.length} variant="success" tone="light" />
                <MapStatusPill label="Belum Terhubung" value={unlinkedCustomersForView.length} variant="warning" tone="light" />
              </div>
            </div>
            )}

            {(sidePanelTab === 'asset' || sidePanelTab === 'draft') && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-semibold mb-3">Daftar OLT ({ftthContext === 'live' ? 'Live' : 'Planning'})</h3>
              <div className="space-y-2 max-h-48 overflow-auto">
                {contextOlts.map((olt) => (
                  <div key={olt.id} className="flex items-center justify-between bg-[#0A0A0A] rounded p-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm">{olt.name}</p>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full border"
                          style={{
                            color: getInfraCategoryTheme(olt.kriteria).text,
                            borderColor: getInfraCategoryTheme(olt.kriteria).border,
                            backgroundColor: getInfraCategoryTheme(olt.kriteria).bg,
                          }}
                        >
                          {getInfraCategoryLabel(olt.kriteria)}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs">{olt.code || '-'} (Lat {formatCoordinate(olt.latitude)}, Lon {formatCoordinate(olt.longitude)})</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditOlt(olt)} className="text-blue-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteOlt(olt.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {(sidePanelTab === 'asset' || sidePanelTab === 'draft') && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-semibold mb-3">Daftar ODP ({ftthContext === 'live' ? 'Live' : 'Planning'})</h3>
              <div className="space-y-2 max-h-56 overflow-auto">
                {contextOdps.map((odp) => (
                  <div key={odp.id} className="flex items-center justify-between bg-[#0A0A0A] rounded p-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm">{odp.name}</p>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full border"
                          style={{
                            color: getInfraCategoryTheme(odp.kriteria).text,
                            borderColor: getInfraCategoryTheme(odp.kriteria).border,
                            backgroundColor: getInfraCategoryTheme(odp.kriteria).bg,
                          }}
                        >
                          {getInfraCategoryLabel(odp.kriteria)}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs">{odp.code || '-'} (Lat {formatCoordinate(odp.latitude)}, Lon {formatCoordinate(odp.longitude)})</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditOdp(odp)} className="text-blue-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteOdp(odp.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {sidePanelTab === 'validation' && (
              <div className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3">
                <h3 className="text-white font-semibold">Validation / Gap</h3>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded border border-red-500/30 bg-red-500/10 p-2 text-red-300">High: {validationSummary.high}</div>
                  <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-amber-300">Medium: {validationSummary.medium}</div>
                  <div className="rounded border border-cyan-500/30 bg-cyan-500/10 p-2 text-cyan-300">Low: {validationSummary.low}</div>
                </div>
                <div className="max-h-56 overflow-auto space-y-2">
                  {validationIssues.slice(0, 24).map((issue, idx) => (
                    <div key={`${issue.id || issue.name}-${idx}`} className="rounded border border-white/10 bg-[#0A0A0A] p-2">
                      <p className="text-xs text-white">{issue.type}: {issue.name || '-'}</p>
                      <p className="text-[11px] text-slate-400">{issue.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidePanelTab === 'draft' && (
              <div className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-2">
                <h3 className="text-white font-semibold">Draft Details</h3>
                <p className="text-xs text-slate-400">Aktifkan Planning lalu pilih Tambah OLT/ODP atau AI Rekomendasi ODP. Klik map untuk set koordinat.</p>
                <p className="text-xs text-slate-300">Aksi: <span className="text-white">{planningAction === 'olt' ? 'OLT' : planningAction === 'odp' ? 'ODP' : planningAction === 'ai-odp' ? 'AI Rekomendasi ODP' : '-'}</span></p>
                <p className="text-xs text-slate-300">Koordinat OLT: <span className="text-cyan-300">{formatCoordinate(oltForm.latitude)}, {formatCoordinate(oltForm.longitude)}</span></p>
                <p className="text-xs text-slate-300">Koordinat ODP: <span className="text-amber-300">{formatCoordinate(odpForm.latitude)}, {formatCoordinate(odpForm.longitude)}</span></p>
                <p className="text-xs text-slate-300">Kapasitas ODP Draft: <span className="text-cyan-300">{Number(odpForm.suggested_capacity || DEFAULT_ODP_CAPACITY)}</span></p>

                {planningAction === 'ai-odp' && (
                  <div className="space-y-3 pt-2 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-300">Kandidat AI ({aiPlanningCandidates.length})</p>
                      <Button type="button" size="sm" variant="outline" className="btn-secondary h-7" onClick={handleRunAiPlanning}>Analisa Ulang</Button>
                    </div>
                    {aiPlanningCandidates.length === 0 ? (
                      <p className="text-xs text-slate-400">Belum ada kandidat. Jalankan Analisa Area pada bar AI Planning.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-auto pr-1">
                        {aiPlanningCandidates.map((candidate) => {
                          const selected = aiPlanningSelectedCandidateId === candidate.id;
                          return (
                            <div key={candidate.id} className={`rounded-lg border p-2 ${selected ? 'border-cyan-400/40 bg-cyan-500/10' : 'border-white/10 bg-black/20'}`}>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm text-white font-semibold">{candidate.label}</p>
                                <span className="text-[11px] rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-amber-200">Score {candidate.totalScore}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-300 mt-1">
                                <p>Kapasitas: <span className="text-cyan-300 font-semibold">{candidate.suggestedCapacity}</span></p>
                                <p>Rumah: <span className="text-white">{candidate.housesCovered}</span></p>
                                <p>Jarak: <span className="text-white">{candidate.distanceFromAnchor} m</span></p>
                                <p>Overlap: <span className="text-white">{Math.round(candidate.overlapRatio * 100)}%</span></p>
                                <p>Density: <span className="text-white">{candidate.densityScore}</span></p>
                                <p>Coverage: <span className="text-white">{candidate.coverageScore}</span></p>
                              </div>
                              <div className="space-y-1 mt-2">
                                {candidate.reasons.map((reason, index) => (
                                  <p key={`${candidate.id}-reason-${index}`} className="text-[11px] text-slate-300">• {reason}</p>
                                ))}
                                {candidate.warnings.map((warning, index) => (
                                  <p key={`${candidate.id}-warning-${index}`} className="text-[11px] text-amber-300">⚠ {warning}</p>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                <Button type="button" size="sm" className="btn-primary h-7 px-2 text-white" onClick={() => handleApplyAiCandidate(candidate)}>Gunakan Titik Ini</Button>
                                <Button type="button" size="sm" variant="outline" className="btn-secondary h-7 px-2" onClick={() => handleToggleCandidateCompare(candidate.id)}>Bandingkan</Button>
                                <Button type="button" size="sm" variant="outline" className="btn-secondary h-7 px-2" onClick={() => handleSimulateAiCandidate(candidate)}>Simulasikan</Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {aiPlanningComparedCandidates.length >= 2 && (
                      <div className="rounded-lg border border-white/10 bg-black/20 p-2 space-y-2">
                        <p className="text-xs text-cyan-200 font-semibold">Perbandingan Kandidat</p>
                        <div className="grid grid-cols-1 gap-2">
                          {aiPlanningComparedCandidates.map((candidate) => (
                            <div key={`compare-${candidate.id}`} className="rounded border border-white/10 bg-[#0A0A0A] p-2 text-[11px] text-slate-300">
                              <p className="text-white font-semibold">{candidate.label} • ODP {candidate.suggestedCapacity}</p>
                              <p>Skor {candidate.totalScore} | Rumah {candidate.housesCovered} | Jarak {candidate.distanceFromAnchor} m</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {sidePanelTab === 'asset' && ftthContext === 'live' && (
            <div className="bg-[#121212] rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-semibold mb-2">Pelanggan Belum Terhubung ODP</h3>
              <div className="max-h-40 overflow-auto space-y-1">
                {unlinkedCustomersForView.length === 0 ? (
                  <p className="text-gray-500 text-sm">Semua pelanggan sudah punya mapping ODP.</p>
                ) : unlinkedCustomersForView.map((c) => (
                  <p key={c.id} className="text-yellow-400 text-xs">{c.customer_id} - {c.name} (ODP: {c.odp || '-'})</p>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>
        </div>

        {!isMapFullscreen && (
        <div className="bg-[#121212] rounded-xl border border-white/10 p-3 flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-semibold">Workspace Explorer (Sekunder)</p>
            <p className="text-xs text-slate-400">Dipisah dari area map utama agar fokus kerja tetap di atas.</p>
          </div>
          <Button type="button" variant="outline" className="btn-secondary" onClick={() => setShowExplorerWorkspace((prev) => !prev)}>
            {showExplorerWorkspace ? 'Sembunyikan Explorer' : 'Tampilkan Explorer'}
          </Button>
        </div>
        )}

        {!isMapFullscreen && showExplorerWorkspace && (
        <div className="bg-[#121212] rounded-xl border border-white/10 p-5 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="text-white font-semibold text-lg">Mode Skalabilitas FTTH</h2>
              <p className="text-gray-400 text-sm">Map overview tetap dipakai untuk gambaran umum. Untuk ODP yang terus bertambah, gunakan explorer dan validasi agar pencarian lebih cepat.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant={viewMode === 'explorer' ? 'default' : 'outline'} className={viewMode === 'explorer' ? 'bg-brand-blue hover:bg-brand-blue/90 text-white' : 'btn-secondary'} onClick={() => setViewMode('explorer')}>Explorer</Button>
              <Button type="button" variant={viewMode === 'validation' ? 'default' : 'outline'} className={viewMode === 'validation' ? 'bg-brand-blue hover:bg-brand-blue/90 text-white' : 'btn-secondary'} onClick={() => setViewMode('validation')}>Validasi</Button>
              <Button type="button" variant="outline" className="btn-secondary" onClick={() => navigate('/dadu/ftth?view=cluster')}>Cluster per OLT</Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-[320px,1fr] gap-4 items-start">
            <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4 space-y-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Pencarian Cepat</p>
              <Input value={explorerQuery} onChange={(event) => setExplorerQuery(event.target.value)} placeholder="Cari OLT, ODP, pelanggan, lokasi..." className="bg-[#050505] border-white/10 text-white" />
              <Select value={explorerOltId} onValueChange={(value) => { setExplorerOltId(value); setExplorerOdpId('all'); }}>
                <SelectTrigger className="bg-[#050505] border-white/10 text-white">
                  <SelectValue placeholder="Filter OLT (Explorer)" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all" className="text-white">Semua OLT</SelectItem>
                  {explorerOltOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={explorerOdpId} onValueChange={setExplorerOdpId}>
                <SelectTrigger className="bg-[#050505] border-white/10 text-white">
                  <SelectValue placeholder="Filter ODP (Explorer)" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all" className="text-white">Semua ODP</SelectItem>
                  {explorerOdpOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                className={explorerIssueOnly ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setExplorerIssueOnly((prev) => !prev)}
              >
                {explorerIssueOnly ? 'Hanya Issue: ON' : 'Hanya Issue: OFF'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="btn-secondary"
                onClick={() => {
                  setExplorerQuery('');
                  setExplorerOltId('all');
                  setExplorerOdpId('all');
                  setExplorerIssueOnly(false);
                }}
              >
                Reset Filter
              </Button>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-white/10 bg-[#050505] p-3"><p className="text-gray-500">OLT tampil</p><p className="text-white font-semibold mt-1">{explorerOlts.length}</p></div>
                <div className="rounded-lg border border-white/10 bg-[#050505] p-3"><p className="text-gray-500">ODP tampil</p><p className="text-white font-semibold mt-1">{explorerOdps.length}</p></div>
                <div className="rounded-lg border border-white/10 bg-[#050505] p-3"><p className="text-gray-500">Pelanggan</p><p className="text-white font-semibold mt-1">{explorerCustomers.length}</p></div>
                <div className="rounded-lg border border-white/10 bg-[#050505] p-3"><p className="text-gray-500">Issue High</p><p className="text-red-300 font-semibold mt-1">{validationSummary.high}</p></div>
              </div>
            </div>

            {viewMode === 'explorer' ? (
              <div className="grid xl:grid-cols-3 gap-4">
                <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4">
                  <h3 className="text-white font-semibold mb-3">Explorer OLT</h3>
                  <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                    {explorerOlts.length === 0 ? <p className="text-gray-500 text-sm">Tidak ada OLT pada filter ini.</p> : explorerOlts.map((olt) => (
                      <div key={olt.id} className="rounded-lg border border-white/10 bg-[#050505] p-3 space-y-1">
                        <div className="flex items-center justify-between gap-2"><p className="text-white text-sm font-semibold">{olt.name}</p><span className="text-[10px] text-gray-400">{olt.odp_count} ODP</span></div>
                        <p className="text-[11px] text-gray-500">{olt.code || '-'} • {olt.location || '-'}</p>
                        <Button type="button" size="sm" variant="outline" className="btn-secondary w-full" onClick={() => handleEditOlt(olt)}>Edit OLT</Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4">
                  <h3 className="text-white font-semibold mb-3">Explorer ODP</h3>
                  <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                    {explorerOdps.length === 0 ? <p className="text-gray-500 text-sm">Tidak ada ODP pada filter ini.</p> : explorerOdps.map((odp) => (
                      <div key={odp.id} className="rounded-lg border border-white/10 bg-[#050505] p-3 space-y-1">
                        {(() => {
                          const prediction = capacityPrediction.byOdpId[String(odp.id)] || null;
                          const capacityValue = Number(prediction?.capacity || odp.suggested_capacity || DEFAULT_ODP_CAPACITY);
                          const usedValue = Number(prediction?.used || odp.customer_count || 0);
                          return (
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-white text-sm font-semibold">{odp.name}</p>
                              <span className="text-[10px] text-cyan-300">{usedValue}/{capacityValue}</span>
                            </div>
                          );
                        })()}
                        <p className="text-[11px] text-gray-500">OLT: {odp.olt_name}</p>
                        <p className="text-[11px] text-gray-500">{odp.code || '-'} • {odp.address || '-'}</p>
                        <Button type="button" size="sm" variant="outline" className="btn-secondary w-full" onClick={() => handleEditOdp(odp)}>Edit ODP</Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4">
                  <h3 className="text-white font-semibold mb-3">Explorer Pelanggan</h3>
                  <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                    {explorerCustomers.length === 0 ? <p className="text-gray-500 text-sm">Tidak ada pelanggan pada filter ini.</p> : explorerCustomersVisible.map((customer) => (
                      <div key={customer.id} className="rounded-lg border border-white/10 bg-[#050505] p-3 space-y-1">
                        <div className="flex items-center justify-between gap-2"><p className="text-white text-sm font-semibold">{customer.name}</p><span className="text-[10px]" style={{ color: getStatusColor(customer.status) }}>{getConnectionLabel(customer.status)}</span></div>
                        <p className="text-[11px] text-gray-500">{customer.customer_id || '-'} • {customer.linked_odp_name}</p>
                        <p className="text-[11px] text-gray-500">RX/Redaman: {formatSignal(customer)}</p>
                        <Button type="button" size="sm" variant="outline" className="btn-secondary w-full" onClick={() => handleEditCustomer(customer)}>Edit Pelanggan</Button>
                      </div>
                    ))}
                    {explorerCustomers.length > explorerCustomersVisible.length && (
                      <Button
                        type="button"
                        variant="outline"
                        className="btn-secondary w-full"
                        onClick={() => setExplorerCustomerLimit((prev) => prev + EXPLORER_CUSTOMER_PAGE_SIZE)}
                      >
                        Muat lagi ({explorerCustomers.length - explorerCustomersVisible.length} sisa)
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4"><p className="text-red-200 text-xs">Issue High</p><p className="text-red-300 text-2xl font-bold">{validationSummary.high}</p></div>
                  <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4"><p className="text-yellow-100 text-xs">Issue Medium</p><p className="text-yellow-300 text-2xl font-bold">{validationSummary.medium}</p></div>
                  <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-4"><p className="text-cyan-100 text-xs">Issue Low</p><p className="text-cyan-300 text-2xl font-bold">{validationSummary.low}</p></div>
                </div>

                <div className="grid xl:grid-cols-[1.2fr,0.8fr] gap-4">
                  <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4">
                    <h3 className="text-white font-semibold mb-3">Daftar Validasi</h3>
                    <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                      {validationIssues.length === 0 ? <p className="text-green-300 text-sm">Tidak ada issue pada filter saat ini.</p> : validationIssues.map((issue, index) => (
                        <div key={`${issue.id || issue.name}-${index}`} className={`rounded-lg border p-3 ${issue.severity === 'high' ? 'border-red-500/30 bg-red-500/10' : issue.severity === 'medium' ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-cyan-500/20 bg-cyan-500/10'}`}>
                          <div className="flex items-center justify-between gap-2"><p className="text-white text-sm font-semibold">{issue.type}: {issue.name || '-'}</p><span className="text-[10px] uppercase text-gray-300">{issue.severity}</span></div>
                          <p className="text-[11px] text-gray-300 mt-1">{issue.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#0A0A0A] rounded-xl border border-white/10 p-4">
                    <h3 className="text-white font-semibold mb-3">Utilisasi ODP</h3>
                    <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                      {explorerOdps.length === 0 ? <p className="text-gray-500 text-sm">Belum ada ODP.</p> : explorerOdps.map((odp) => (
                        <div key={odp.id} className="rounded-lg border border-white/10 bg-[#050505] p-3">
                          {(() => {
                            const prediction = capacityPrediction.byOdpId[String(odp.id)] || null;
                            const capacityValue = Number(prediction?.capacity || odp.suggested_capacity || DEFAULT_ODP_CAPACITY);
                            const usedValue = Number(prediction?.used || odp.customer_count || 0);
                            const tone = prediction?.risk?.tone || (usedValue > capacityValue ? 'critical' : usedValue === capacityValue ? 'warning' : 'safe');
                            return (
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-white text-sm font-semibold">{odp.name}</p>
                                <span className={`text-[10px] ${tone === 'critical' ? 'text-red-300' : tone === 'warning' ? 'text-yellow-300' : tone === 'high' ? 'text-orange-300' : 'text-cyan-300'}`}>{usedValue}/{capacityValue}</span>
                              </div>
                            );
                          })()}
                          <p className="text-[11px] text-gray-500 mt-1">OLT: {odp.olt_name}</p>
                          {(() => {
                            const prediction = capacityPrediction.byOdpId[String(odp.id)] || null;
                            const capacityValue = Number(prediction?.capacity || odp.suggested_capacity || DEFAULT_ODP_CAPACITY);
                            const usedValue = Number(prediction?.used || odp.customer_count || 0);
                            const remainingValue = Math.max(0, capacityValue - usedValue);
                            return (
                              <p className="text-[11px] text-gray-500">Sisa slot: {remainingValue} • Risk: {prediction?.risk?.label || 'Aman'}</p>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </main>
      <AdminGlobalMenuBar desktop onlyWhenFullscreen />
    </div>
  );
};

export default AdminFtthDesign;






























